import useOverviewHistory, { clearOverviewHistory } from './dashboard/useOverviewHistory';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { showSuccess, showError, showWarning, showInfo } from '../utils/notifications';

// Import Google Analytics utilities
import {
  trackFlowTestCreation,
  trackFlowTestExecution,
  trackFeatureUsage
} from '../utils/analytics';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import IDTooltip from './IDTooltip';
import LiveRecorder from './LiveRecorder';
import VNCLiveRecorder from './VNCLiveRecorder';
import TestVariablesManager from './TestVariablesManager';
import FlowTestEditor from './FlowTestEditor';
import { 
  Play,
  Plus,
  Settings,
  Globe,
  Code,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Brain,
  Zap,
  Copy,
  Eye,
  EyeOff,
  Monitor,
  Smartphone,
  Tablet,
  Chrome,
  RefreshCw,
  Image as ImageIcon,
  Video,
  Download,
  Upload,
  Target,
  Camera,
  Pause,
  Square,
  RotateCcw,
  Calendar,
  ChevronDown,
  ChevronUp,
  Search,
  X as XIcon,
  Folder,
  Bug,
  FileText,
  ExternalLink,
  Loader2,
  Database,
  User,
  X,
  Hash
} from 'lucide-react';

// Backend URL imported from utility
// const BACKEND_URL is imported at the top

// Debug mode - set to false to disable console.log messages
const DEBUG_MODE = false;
const debugLog = (...args) => {
  if (DEBUG_MODE) debugLog(...args);
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Enhanced axios interceptor for Flow Testing
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors globally
    if (error.response?.status === 401 && error.config?.url?.includes('flow-testing')) {
      console.warn('Flow Testing authentication error detected');
      // Don't auto-redirect, just log the error for user to handle
    }
    
    // Handle HTML responses when JSON expected
    if (error.response && error.response.data && typeof error.response.data === 'string' && 
        (error.response.data.includes('<!DOCTYPE') || error.response.data.includes('<html'))) {
      console.warn('Received HTML error page in Flow Testing:', error.response.data);
      error.message = 'Server error - please refresh the page and try again';
    }
    return Promise.reject(error);
  }
);

// Flow Testing Error Boundary
class FlowTestingErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('FlowTesting Error Boundary caught an error:', error, errorInfo);
    console.error('Error message:', error?.message);
    console.error('Error stack:', error?.stack);
    this.setState({ error: error });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6">
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription>
              <div className="text-red-800">
                <h3 className="font-semibold mb-2">Something went wrong with Flow Testing</h3>
                <p className="mb-3">Please refresh the page to continue. If the problem persists, please contact support.</p>
                <Button 
                  onClick={() => window.location.reload()} 
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Refresh Page
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}


// Live Screenshot Viewer Component
const LiveScreenshotViewer = ({ executionId }) => {
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Use top-level BACKEND_URL from module scope (no HTTP fallback)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchLiveScreenshot = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/executions/${executionId}/live-screenshot`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob',
          // Add timestamp to prevent caching
          params: { t: Date.now() }
        }
      );
      
      // Create blob URL
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      
      // Revoke old URL to prevent memory leaks
      if (screenshotUrl) {
        URL.revokeObjectURL(screenshotUrl);
      }
      
      setScreenshotUrl(url);
      setLoading(false);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Connecting to live stream... Please wait.');
      } else {
        setError('Live stream temporarily unavailable');
      }
      setLoading(false);
    }
  };

  // Auto-refresh every 800ms for smooth live streaming feel
  useEffect(() => {
    fetchLiveScreenshot(); // Initial fetch
    
    const interval = setInterval(() => {
      fetchLiveScreenshot();
    }, 800);  // Faster updates for smoother streaming

    return () => {
      clearInterval(interval);
      if (screenshotUrl) {
        URL.revokeObjectURL(screenshotUrl);
      }
    };
  }, [executionId]);

  if (loading && !screenshotUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-white">
        <RefreshCw className="h-8 w-8 animate-spin mb-3" />
        <p>Loading live view...</p>
      </div>
    );
  }

  if (error && !screenshotUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-white">
        <AlertTriangle className="h-8 w-8 mb-3 text-yellow-500" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {screenshotUrl && (
        <img
          src={screenshotUrl}
          alt="Live execution view"
          className="w-full h-full object-contain transition-opacity duration-300 ease-in-out"
          style={{ opacity: 1 }}
        />
      )}
      {/* Live indicator overlay */}
      <div className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs rounded-full flex items-center space-x-1 animate-pulse">
        <div className="h-2 w-2 rounded-full bg-white"></div>
        <span>LIVE</span>
      </div>
      {/* Refresh rate indicator */}
      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black bg-opacity-50 text-white text-xs rounded flex items-center space-x-1">
        <div className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse"></div>
        <span>Real-time</span>
      </div>
    </div>
  );
};

// Live Steps Feed Component
const LiveStepsFeed = ({ executionId, testName }) => {
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const stepsEndRef = useRef(null);
  // Use top-level BACKEND_URL from module scope (no HTTP fallback)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchExecutionStatus = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/executions/${executionId}`,
        { headers: getAuthHeaders() }
      );
      setExecution(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch execution status:', err);
    }
  };

  // Auto-refresh every 1 second for step updates
  useEffect(() => {
    fetchExecutionStatus(); // Initial fetch
    
    const interval = setInterval(() => {
      fetchExecutionStatus();
    }, 1000);

    return () => clearInterval(interval);
  }, [executionId]);

  // Auto-scroll to latest step
  useEffect(() => {
    if (stepsEndRef.current) {
      stepsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [execution?.results?.length]);

  const getStepIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStepStatusColor = (status) => {
    switch (status) {
      case 'passed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      case 'running':
        return 'bg-blue-50 border-blue-200 animate-pulse';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (loading && !execution) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <h3 className="font-semibold text-sm text-gray-900 truncate">{testName}</h3>
        <div className="flex items-center space-x-2 mt-1">
          <span className="text-xs text-gray-600">
            {execution?.results?.length || 0} steps completed
          </span>
          <div className="flex items-center space-x-1">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-xs text-green-600">Updating</span>
          </div>
        </div>
      </div>

      {/* Steps Feed - Chat style */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {execution?.results && execution.results.length > 0 ? (
          <>
            {execution.results.map((result, index) => (
              <div
                key={result.step_id || index}
                className={`p-3 rounded-lg border ${getStepStatusColor(result.status)} transition-all duration-300`}
              >
                <div className="flex items-start space-x-2">
                  <div className="mt-0.5 flex-shrink-0">
                    {getStepIcon(result.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 break-words">
                      {result.message || result.description || `Step ${index + 1}`}
                    </p>
                    {result.execution_time_ms && (
                      <p className="text-xs text-gray-500 mt-1">
                        {result.execution_time_ms}ms
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={stepsEndRef} />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
            <Clock className="h-8 w-8 mb-2" />
            <p className="text-sm">Waiting for test to start...</p>
          </div>
        )}
      </div>

      {/* Footer Status */}
      <div className="p-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">
            Status: <span className="font-medium capitalize">{execution?.status || 'Running'}</span>
          </span>
          <span className="text-gray-500">
            {execution?.started_at && new Date(execution.started_at).toLocaleTimeString()}
          </span>
        </div>
      </div>
    </div>
  );
};

// Replay Screenshot Viewer Component
const ReplayScreenshotViewer = ({ execution, currentIndex }) => {
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Use top-level BACKEND_URL from module scope (no HTTP fallback)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchScreenshot = async () => {
      if (!execution?.screenshots || !execution.screenshots[currentIndex]) {
        setError('No screenshot available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Get screenshot filename from path
        const screenshotPath = execution.screenshots[currentIndex];
        debugLog('Loading screenshot:', screenshotPath);
        const filename = screenshotPath.split('/').pop();
        debugLog('Filename:', filename);
        
        const response = await axios.get(
          `${BACKEND_URL}/api/flow-testing/screenshot-data/${filename}`,
          { 
            headers: getAuthHeaders()
          }
        );
        
        // Endpoint returns full data URL (already includes 'data:image/png;base64,' prefix)
        const dataUrl = response.data.data;
        
        if (screenshotUrl) {
          URL.revokeObjectURL(screenshotUrl);
        }
        
        setScreenshotUrl(dataUrl);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load screenshot:', err);
        setError('Failed to load screenshot');
        setLoading(false);
      }
    };

    fetchScreenshot();

    return () => {
      // No need to revoke data URLs
    };
  }, [currentIndex, execution]);

  if (loading && !screenshotUrl) {
    return (
      <div className="flex items-center justify-center h-full text-white">
        <RefreshCw className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error && !screenshotUrl) {
    return (
      <div className="flex items-center justify-center h-full text-white">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mb-2 mx-auto" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {screenshotUrl && (
        <img
          src={screenshotUrl}
          alt="Replay view"
          className="w-full h-full object-contain transition-opacity duration-300 ease-in-out"
        />
      )}
      {/* Replay indicator */}
      <div className="absolute top-2 right-2 px-2 py-1 bg-purple-600 text-white text-xs rounded-full flex items-center space-x-1">
        <Play className="h-3 w-3" />
        <span>REPLAY</span>
      </div>
      {/* Frame counter */}
      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black bg-opacity-50 text-white text-xs rounded">
        Frame {currentIndex + 1} / {execution?.screenshots?.length || 0}
      </div>
    </div>
  );
};

// Replay Steps Feed Component  
const ReplayStepsFeed = ({ execution, currentStepIndex }) => {
  const stepsEndRef = useRef(null);

  useEffect(() => {
    if (stepsEndRef.current) {
      stepsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentStepIndex]);

  const getStepIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStepStatusColor = (status, isActive) => {
    if (isActive) {
      return 'bg-blue-50 border-blue-300 animate-pulse';
    }
    switch (status) {
      case 'passed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const visibleResults = execution?.results?.slice(0, currentStepIndex + 1) || [];

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <h3 className="font-semibold text-sm text-gray-900 truncate">{execution?.test_name || 'Test Replay'}</h3>
        <div className="flex items-center space-x-2 mt-1">
          <span className="text-xs text-gray-600">
            Step {currentStepIndex + 1} of {execution?.results?.length || 0}
          </span>
          <div className="flex items-center space-x-1">
            <Play className="h-3 w-3 text-purple-600" />
            <span className="text-xs text-purple-600">Replaying</span>
          </div>
        </div>
      </div>

      {/* Steps Feed */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {visibleResults.length > 0 ? (
          <>
            {visibleResults.map((result, index) => {
              const isActive = index === currentStepIndex;
              return (
                <div
                  key={result.step_id || index}
                  className={`p-3 rounded-lg border ${getStepStatusColor(result.status, isActive)} transition-all duration-300`}
                >
                  <div className="flex items-start space-x-2">
                    <div className="mt-0.5 flex-shrink-0">
                      {isActive ? <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" /> : getStepIcon(result.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 break-words">
                        {result.message || result.description || `Step ${index + 1}`}
                      </p>
                      {result.execution_time_ms && (
                        <p className="text-xs text-gray-500 mt-1">
                          {result.execution_time_ms}ms
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={stepsEndRef} />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
            <Clock className="h-8 w-8 mb-2" />
            <p className="text-sm">No steps to replay</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">
            Status: <span className="font-medium capitalize">{execution?.status || 'Completed'}</span>
          </span>
          <span className="text-gray-500">
            {execution?.finished_at && new Date(execution.finished_at).toLocaleTimeString()}
          </span>
        </div>
      </div>
    </div>
  );
};

// Replay Modal Component with Playback Controls
const ReplayModal = ({ execution, onClose, testName }) => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [fullExecution, setFullExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  // Use top-level BACKEND_URL from module scope (no HTTP fallback)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch full execution data with screenshots
  useEffect(() => {
    const fetchFullExecution = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/flow-testing/executions/${execution.id}`,
          { headers: getAuthHeaders() }
        );
        setFullExecution(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load execution:', err);
        setLoading(false);
      }
    };
    fetchFullExecution();
  }, [execution.id]);

  const totalFrames = fullExecution?.screenshots?.length || 0;
  const totalSteps = fullExecution?.results?.length || 0;

  // Calculate which step should be active based on cumulative execution time
  const getStepIndexForFrame = (frameIndex) => {
    if (!fullExecution?.results || totalFrames === 0) return 0;
    
    // Calculate total execution time
    const totalExecutionTime = fullExecution.duration_ms || 0;
    if (totalExecutionTime === 0) {
      // Fallback to linear distribution if no timing data
      const linearStep = Math.floor((frameIndex / totalFrames) * totalSteps);
      return Math.min(linearStep, totalSteps - 1);
    }
    
    // Calculate elapsed time for this frame (assuming even frame distribution over total duration)
    const elapsedTime = (frameIndex / Math.max(1, totalFrames - 1)) * totalExecutionTime;
    
    // Find which step should be active at this elapsed time
    let cumulativeTime = 0;
    for (let i = 0; i < fullExecution.results.length; i++) {
      const result = fullExecution.results[i];
      const stepTime = result.execution_time_ms || 0;
      cumulativeTime += stepTime;
      
      if (elapsedTime <= cumulativeTime) {
        return i;
      }
    }
    
    // If elapsed time exceeds all steps, return last step
    return Math.max(0, totalSteps - 1);
  };

  // Auto-play through screenshots and steps
  useEffect(() => {
    if (!isPlaying || totalFrames === 0) return;

    const interval = setInterval(() => {
      setCurrentFrame(prev => {
        if (prev >= totalFrames - 1) {
          setIsPlaying(false);
          return prev;
        }
        const nextFrame = prev + 1;
        
        // Update step based on frame timing
        const stepIndex = getStepIndexForFrame(nextFrame);
        setCurrentStep(stepIndex);
        
        return nextFrame;
      });
    }, 500 / playbackSpeed); // Base 500ms per frame

    return () => clearInterval(interval);
  }, [isPlaying, currentFrame, totalFrames, totalSteps, playbackSpeed, fullExecution]);

  const handlePlayPause = () => {
    if (totalFrames === 0) {
      showWarning('No screenshots available for replay');
      return;
    }
    if (currentFrame >= totalFrames - 1) {
      setCurrentFrame(0);
      setCurrentStep(0);
    }
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    setCurrentFrame(0);
    setCurrentStep(0);
    setIsPlaying(false);
  };

  const handleFrameChange = (value) => {
    setCurrentFrame(value);
    const stepIndex = getStepIndexForFrame(value);
    setCurrentStep(stepIndex);
  };

  if (loading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center justify-center p-8">
            <RefreshCw className="h-8 w-8 animate-spin text-purple-600 mb-4" />
            <p className="text-gray-600">Loading replay...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!fullExecution || totalFrames === 0) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>No Replay Available</DialogTitle>
            <DialogDescription>
              This execution doesn't have replay data available.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4">
            <p className="text-gray-600">No screenshots are available for this execution. Screenshots are only captured during headed mode execution.</p>
            <Button onClick={onClose} className="mt-4">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b border-gray-200">
          <DialogTitle className="text-lg font-bold">
            <div className="flex items-center space-x-2">
              <Play className="h-5 w-5 text-purple-600" />
              <span>Replay - Execution #{fullExecution.execution_number}</span>
              <Badge className="bg-purple-100 text-purple-700">
                <div className="flex items-center space-x-1">
                  <span>Replay</span>
                </div>
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {/* Playback Controls - Moved below header */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleRestart}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                onClick={handlePlayPause}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isPlaying ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
            </div>
            
            {/* Speed Control */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Speed:</span>
              <select
                value={playbackSpeed}
                onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
                className="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white"
              >
                <option value={0.5}>0.5x</option>
                <option value={1}>1x</option>
                <option value={1.5}>1.5x</option>
                <option value={2}>2x</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden px-6 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Left: Replay Browser View */}
            <div className="lg:col-span-2 flex flex-col h-full min-h-0">
              <div className="bg-gray-900 rounded-lg p-4 flex-1 min-h-0">
                <div className="h-full bg-gray-800 rounded flex items-center justify-center relative overflow-hidden">
                  <ReplayScreenshotViewer execution={fullExecution} currentIndex={currentFrame} />
                </div>
              </div>

              {/* Playback Timeline */}
              <div className="mt-4 space-y-2">
                <input
                  type="range"
                  min="0"
                  max={totalFrames - 1}
                  value={currentFrame}
                  onChange={(e) => handleFrameChange(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <div className="flex items-center space-x-3">
                    <span>Frame {currentFrame + 1} / {totalFrames}</span>
                    <span className="text-blue-600 font-medium">
                      Step {currentStep + 1} / {totalSteps}
                    </span>
                  </div>
                  <span>Speed: {playbackSpeed}x</span>
                </div>
              </div>

              {/* Execution Info */}
              <div className="grid grid-cols-3 gap-4 text-sm mt-4">
                <div>
                  <span className="text-gray-600">Status:</span>
                  <Badge className="ml-2">{fullExecution.status}</Badge>
                </div>
                <div>
                  <span className="text-gray-600">Browser:</span>
                  <span className="ml-2 capitalize">{fullExecution.browser}</span>
                </div>
                <div>
                  <span className="text-gray-600">Duration:</span>
                  <span className="ml-2">{fullExecution.duration_ms}ms</span>
                </div>
              </div>
            </div>

            {/* Right: Replay Steps Feed */}
            <div className="lg:col-span-1 h-full min-h-0">
              <ReplayStepsFeed execution={fullExecution} currentStepIndex={currentStep} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FlowTesting = ({ user, selectedTestFromDashboard, onTestSelected }) => {
  // State management
  const [flowTests, setFlowTests] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEnvironmentModal, setShowEnvironmentModal] = useState(false);
  const [showEditEnvironmentModal, setShowEditEnvironmentModal] = useState(false);
  const [editingEnvironment, setEditingEnvironment] = useState(null);
  const [showLiveRecorder, setShowLiveRecorder] = useState(false);
  const [showVNCRecorder, setShowVNCRecorder] = useState(false);
  const [testToResume, setTestToResume] = useState(null);
  
  // Track test IDs that returned 404 to avoid repeated API calls
  const invalidTestIds = useRef(new Set());
  
  // P1 flood fix: prevent overlapping running-executions polls from piling up
  const runningExecsInFlight = useRef(false);
  
  // Pagination state for infinite scroll (tests)
  const [hasMoreTests, setHasMoreTests] = useState(true);
  const [testsOffset, setTestsOffset] = useState(0);
  const [loadingMoreTests, setLoadingMoreTests] = useState(false);
  
  // Pagination state for executions
  const [hasMoreExecutions, setHasMoreExecutions] = useState(true);
  const [executionsOffset, setExecutionsOffset] = useState(0);
  const [loadingMoreExecutions, setLoadingMoreExecutions] = useState(false);
  
  // Running executions tracking for test list
  const [runningExecutionsByTest, setRunningExecutionsByTest] = useState({});
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  
  // Project state (Phase 2: Non-breaking)
  const [projects, setProjects] = useState([]);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  
  // Test Segments state
  const [testSegments, setTestSegments] = useState([]);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [showCreateSegmentModal, setShowCreateSegmentModal] = useState(false);
  const [showInsertSegmentModal, setShowInsertSegmentModal] = useState(false);
  const [selectedStepsForSegment, setSelectedStepsForSegment] = useState([]);
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [executionTest, setExecutionTest] = useState(null);
  const [executionConfig, setExecutionConfig] = useState({
    browser: 'chromium',
    device_type: 'desktop',
    device_name: null,
    environment_id: null,
    credential_profile_id: null,
    is_headless: true,
    debug_mode: false,
    parallel_executions: 1,
    viewport_width: 1920,
    viewport_height: 1080
  });
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showExecutionDetails, setShowExecutionDetails] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showLiveView, setShowLiveView] = useState(false);
  const [liveViewExecution, setLiveViewExecution] = useState(null);
  const [showReplayView, setShowReplayView] = useState(false);
  const [replayExecution, setReplayExecution] = useState(null);
  
  // Get sub-tab from URL or default to 'tests'
  const getInitialSubTab = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'tests';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialSubTab);
  
  // Update URL when sub-tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(window.location.search);
    if (tab !== 'executions') clearOverviewHistory(params);
    params.set('tab', tab);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ tab }, '', newUrl);
    
    // Auto-load schedules when switching to schedules tab
    if (tab === 'schedules' && schedules.length === 0 && !loadingSchedules) {
      loadSchedules();
    }
  };
  
  // Enhanced step management state
  const [stepPrompt, setStepPrompt] = useState('');
  const [addingStep, setAddingStep] = useState(false);
  
  // Web element editing state
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [showSelectorModal, setShowSelectorModal] = useState(false);
  const [selectorForm, setSelectorForm] = useState({
    type: 'css',
    value: '',
    fallback_selectors: [],
    stepTitle: '',
    timeout: 15000 // Default 15 seconds in milliseconds
  });
  
  // Segment creation from steps state
  const [segmentForm, setSegmentForm] = useState({
    name: '',
    description: '',
    segment_type: 'generic',
    tags: []
  });
  const [showCreateSegmentFromStepsModal, setShowCreateSegmentFromStepsModal] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [lastExecutionId, setLastExecutionId] = useState(null);

  // Schedule states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedTestForSchedule, setSelectedTestForSchedule] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    schedule_type: 'one_time',
    scheduled_datetime: '',
    recurrence_pattern: 'daily',
    start_date: '',
    end_date: '',
    time_of_day: '09:00',
    timezone: 'America/New_York',
    email_notifications: true,
    in_app_notifications: true
  });

  // Jira integration states
  const [jiraIntegrations, setJiraIntegrations] = useState([]);
  const [creatingJiraTicket, setCreatingJiraTicket] = useState(false);

  // Delete confirmation modal state  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  
  // Additional confirmation modal states
  const [showCancelExecutionModal, setShowCancelExecutionModal] = useState(false);
  const [executionToCancel, setExecutionToCancel] = useState(null);
  const [showDeleteEnvironmentModal, setShowDeleteEnvironmentModal] = useState(false);
  const [environmentToDelete, setEnvironmentToDelete] = useState(null);
  const [showDeleteSegmentModal, setShowDeleteSegmentModal] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [showDeleteStepModal, setShowDeleteStepModal] = useState(false);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [showDeleteScheduleModal, setShowDeleteScheduleModal] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  // Test Variables states
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableValue, setNewVariableValue] = useState('');
  const [newVariableIsSensitive, setNewVariableIsSensitive] = useState(false);
  const [variableUsageMap, setVariableUsageMap] = useState({});

  const viewExecutionDetails = async (executionId) => {
    try {
      setLoading(true);
      setShowErrorDetails(false); // Reset error details to collapsed
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/executions/${executionId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedExecution(response.data);
      setShowExecutionDetails(true);
    } catch (error) {
      console.error('Failed to load execution details:', error);
      showError('Failed to load execution details');
    } finally {
      setLoading(false);
    }
  };

  // Form states
  const [testForm, setTestForm] = useState({
    name: '',
    description: '',
    tags: [],
    steps: [],
    global_assertions: [],
    configuration: {
      browser: 'chromium',
      device_type: 'desktop',
      viewport_width: 1920,
      viewport_height: 1080,
      is_headless: true,
      enable_screenshots: true,
      enable_video: false,
      timeout: 30000
    },
    environment_id: null,
    project_id: null, // Phase 2: Project organization
    test_variables: {}, // Test-level variables for reusable data
    sensitive_variables: [] // List of variable names that should be masked
  });

  const [environmentForm, setEnvironmentForm] = useState({
    name: '',
    description: '',
    base_url: '',
    variables: {},
    credential_profiles: []
  });
  const [environmentVariablesText, setEnvironmentVariablesText] = useState('');
  // State for adding/editing credential profiles
  const [credentialProfiles, setCredentialProfiles] = useState([]);
  const [showAddProfileForm, setShowAddProfileForm] = useState(false);
  const [editingProfileIndex, setEditingProfileIndex] = useState(null); // Index of profile being edited
  const [newProfileForm, setNewProfileForm] = useState({
    name: '',
    description: '',
    cookies: '',
    headers: '',
    is_default: false
  });

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiEnvironmentId, setAiEnvironmentId] = useState('');

  // Load data on component mount
  useEffect(() => {
    loadFlowTests();
    loadEnvironments();
    loadTestSegments();
  }, []);

  // Load Jira integrations only when user is available and authenticated
  useEffect(() => {
    if (user && user.email) {
      loadJiraIntegrations();
    }
  }, [user]);

  // Load projects on component mount (Phase 2: Non-breaking)
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoadingProjects(true);
        const response = await axios.get(
          `${BACKEND_URL}/api/projects`,
          { headers: getAuthHeaders() }
        );
        setProjects(response.data || []);
        debugLog('✅ Projects loaded:', response.data?.length || 0);
      } catch (error) {
        console.error('❌ Failed to load projects:', error);
        setProjects([]); // Graceful fallback - continue without projects
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, []); // Run once on mount

  useOverviewHistory('flow_test', selectedTestFromDashboard, onTestSelected, (test) => {
    setSelectedTest(test);
    setActiveTab('executions');
    return loadExecutions(test.id);
  });

  const loadFlowTests = async (loadMore = false, projectIdParam = undefined) => {
    try {
      if (loadMore) {
        setLoadingMoreTests(true);
      } else {
        setTestsOffset(0);
      }
      
      const currentOffset = loadMore ? testsOffset : 0;
      // If projectIdParam is explicitly passed (including null), use it; otherwise use selectedProjectFilter
      const currentProjectId = projectIdParam !== undefined ? projectIdParam : selectedProjectFilter;
      
      // Build query string
      let queryString = `limit=10&offset=${currentOffset}`;
      if (searchQuery) {
        queryString += `&search=${encodeURIComponent(searchQuery)}`;
      }
      if (currentProjectId) {
        queryString += `&project_id=${encodeURIComponent(currentProjectId)}`;
      }
      
      debugLog('ENVIRONMENT DEBUG - loadFlowTests starting...');
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/tests?${queryString}`,
        { headers: getAuthHeaders() }
      );
      
      const tests = response.data.tests || [];
      
      // Log specific test data
      const userWorkflowTest = tests.find(t => t.name === "User Workflow Test");
      if (userWorkflowTest) {
        debugLog('ENVIRONMENT DEBUG - User Workflow Test loaded with environment_id:', userWorkflowTest.environment_id);
      }
      
      debugLog('ENVIRONMENT DEBUG - loadFlowTests received data from backend');
      debugLog('ENVIRONMENT DEBUG - First few tests environment_ids:', 
        tests.slice(0, 3).map(t => `${t.name}: ${t.environment_id}`));
      
      if (loadMore) {
        setFlowTests(prev => [...prev, ...tests]);
      } else {
        setFlowTests(tests);
        // Clear invalid test IDs cache when reloading from scratch
        invalidTestIds.current.clear();
      }
      
      setHasMoreTests(response.data.has_more);
      setTestsOffset(currentOffset + 10);
      
      debugLog(`Loaded ${tests.length} Flow Tests successfully`);
    } catch (error) {
      console.error('Failed to load Flow Tests:', error);
      
      // Enhanced error handling for loading
      if (error.response?.status === 401) {
        console.warn('Authentication failed while loading Flow Tests');
        // Don't show alert for auth errors during loading, just set empty array
        if (!loadMore) {
          setFlowTests([]);
        }
      } else {
        console.warn('Setting empty Flow Tests due to load error');
        if (!loadMore) {
          setFlowTests([]);
        }
      }
    } finally {
      setLoadingMoreTests(false);
    }
  };
  
  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const loadEnvironments = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/environments`,
        { headers: getAuthHeaders() }
      );
      setEnvironments(response.data);
    } catch (error) {
      console.error('Failed to load environments:', error);
      setEnvironments([]);
    }
  };

  // Load Jira integrations for the user
  const loadJiraIntegrations = async () => {
    // Early return if no authentication
    const token = localStorage.getItem('token');
    if (!token) {
      setJiraIntegrations([]);
      return;
    }
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/integrations`,
        { headers: getAuthHeaders() }
      );
      // Filter only active Jira integrations
      const jiraOnly = response.data.filter(
        integration => integration.integration_type === 'jira' && integration.is_active
      );
      setJiraIntegrations(jiraOnly);
    } catch (error) {
      // Don't log errors for missing integrations - this is normal for new users
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        console.error('Failed to load Jira integrations:', error);
      }
      setJiraIntegrations([]);
    }
  };

  // Create Jira ticket from execution failure
  const createJiraTicketFromExecution = async (execution) => {
    if (!execution) return;
    
    // Check if user has Jira integrations
    if (jiraIntegrations.length === 0) {
      showInfo('To create Jira tickets, please configure a Jira integration in the Integrations tab first.');
      return;
    }
    
    // Use the first active Jira integration (can be enhanced to let user choose)
    const jiraIntegration = jiraIntegrations[0];
    
    setCreatingJiraTicket(true);
    
    try {
      // Prepare ticket data
      const ticketData = {
        test_name: execution.test_name || selectedTest?.name || 'Unknown Test',
        test_type: 'Flow Test',
        error_message: execution.error_message || 'Test execution failed',
        console_logs: execution.console_logs?.map(log => `[${log.timestamp}] [${log.type}] ${log.text}`) || [],
        network_logs: execution.network_logs || [],
        screenshots: execution.screenshots?.map(screenshot => {
          // Convert screenshot path to full URL
          const filename = screenshot.split('/').pop();
          return `${BACKEND_URL}/api/flow-testing/screenshot-data/${filename}`;
        }) || [],
        repro_steps: generateReproSteps(execution)
      };
      
      // Make API call to create Jira ticket
      const response = await axios.post(
        `${BACKEND_URL}/api/integrations/${jiraIntegration.id}/create-jira-ticket`,
        ticketData,
        { 
          headers: getAuthHeaders(),
          params: {
            test_name: ticketData.test_name,
            test_type: ticketData.test_type
          }
        }
      );
      
      if (response.data.success) {
        // Show success message with link to Jira ticket
        const issueKey = response.data.issue_key;
        const issueUrl = response.data.issue_url;
        
        // Create success toast
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 max-w-md';
        toast.innerHTML = `
          <div class="flex items-start gap-3">
            <svg class="h-6 w-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <div>
              <div class="font-semibold mb-1">Jira Ticket Created Successfully!</div>
              <div class="text-sm mb-2">Issue ${issueKey} has been created.</div>
              <a href="${issueUrl}" target="_blank" rel="noopener noreferrer" 
                 class="inline-flex items-center gap-1 text-sm underline hover:no-underline">
                View in Jira
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
              </a>
            </div>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
          toast.style.opacity = '0';
          toast.style.transition = 'opacity 0.3s';
          setTimeout(() => toast.remove(), 300);
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to create Jira ticket:', error);
      showError(`Failed to create Jira ticket: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCreatingJiraTicket(false);
    }
  };

  // Generate reproduction steps from execution
  const generateReproSteps = (execution) => {
    const steps = ['**Automated Test Execution Failed**', ''];
    
    steps.push(`**Test Name:** ${execution.test_name || 'Unknown'}`);
    steps.push(`**Execution ID:** ${execution.id}`);
    steps.push(`**Execution Number:** #${execution.execution_number}`);
    steps.push(`**Browser:** ${execution.browser}`);
    steps.push(`**Device Type:** ${execution.device_type}`);
    steps.push(`**Headless Mode:** ${execution.is_headless ? 'Yes' : 'No'}`);
    steps.push('');
    
    if (execution.started_at) {
      steps.push(`**Started At:** ${new Date(execution.started_at).toLocaleString()}`);
    }
    if (execution.finished_at) {
      steps.push(`**Finished At:** ${new Date(execution.finished_at).toLocaleString()}`);
    }
    if (execution.duration_ms) {
      steps.push(`**Duration:** ${(execution.duration_ms / 1000).toFixed(2)}s`);
    }
    steps.push('');
    
    steps.push('**Steps Executed:**');
    if (execution.results && execution.results.length > 0) {
      execution.results.forEach((result, index) => {
        const status = result.status === 'passed' ? '✅' : '❌';
        steps.push(`${index + 1}. ${status} ${result.message || `Step ${index + 1}`}`);
        if (result.execution_time_ms) {
          steps.push(`   - Execution time: ${result.execution_time_ms}ms`);
        }
        if (result.error_details) {
          steps.push(`   - Error: ${JSON.stringify(result.error_details)}`);
        }
      });
    }
    
    return steps.join('\n');
  };

  const loadExecutions = async (testId, reset = true) => {
    try {
      const offset = reset ? 0 : executionsOffset;
      const limit = 10;
      
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/tests/${testId}/executions?limit=${limit}&offset=${offset}`,
        { headers: getAuthHeaders() }
      );
      
      if (reset) {
        setExecutions(response.data);
        setExecutionsOffset(response.data.length);
      } else {
        setExecutions(prev => [...prev, ...response.data]);
        setExecutionsOffset(prev => prev + response.data.length);
      }
      
      // Check if there are more executions to load
      setHasMoreExecutions(response.data.length === limit);
    } catch (error) {
      console.error('Failed to load executions:', error);
      if (reset) {
        setExecutions([]);
      }
    }
  };
  
  const loadMoreExecutions = async () => {
    if (!selectedTest || loadingMoreExecutions || !hasMoreExecutions) return;
    
    setLoadingMoreExecutions(true);
    await loadExecutions(selectedTest.id, false);
    setLoadingMoreExecutions(false);
  };
  
  // Load running executions for all visible tests
  // P1 flood fix: parallelize requests with Promise.all + in-flight guard to prevent overlap
  const loadRunningExecutions = async () => {
    // Skip if a previous poll is still running (prevents request pile-up)
    if (runningExecsInFlight.current) return;
    runningExecsInFlight.current = true;

    try {
      const testsToCheck = flowTests.filter(
        (t) => !invalidTestIds.current.has(t.id)
      );

      const responses = await Promise.all(
        testsToCheck.map((test) =>
          axios
            .get(
              `${BACKEND_URL}/api/flow-testing/tests/${test.id}/executions?limit=5`,
              { headers: getAuthHeaders() }
            )
            .then((response) => ({ test, data: response.data }))
            .catch((testError) => {
              if (testError.response?.status === 404) {
                invalidTestIds.current.add(test.id);
              } else {
                console.warn(
                  `Failed to load executions for test ${test.id}:`,
                  testError.message
                );
              }
              return null;
            })
        )
      );

      const runningByTest = {};
      for (const entry of responses) {
        if (!entry) continue;
        const { test, data } = entry;
        const runningExecs = data.filter(
          (exec) => exec.status === 'running' || exec.status === 'pending'
        );
        if (runningExecs.length > 0) {
          runningByTest[test.id] = {
            count: runningExecs.length,
            executions: runningExecs,
          };
        }
      }
      setRunningExecutionsByTest(runningByTest);
    } catch (error) {
      console.error('Failed to load running executions:', error);
    } finally {
      runningExecsInFlight.current = false;
    }
  };

  // Auto-refresh executions when there are running executions
  useEffect(() => {
    if (!selectedTest || activeTab !== 'executions') return;
    
    // Check if there are any running executions (excluding paused)
    const hasRunningExecutions = executions.some(
      exec => (exec.status === 'running' || exec.status === 'pending') && exec.status !== 'paused'
    );
    
    if (!hasRunningExecutions) return;
    
    // Poll every 3 seconds when there are running executions
    const intervalId = setInterval(() => {
      loadExecutions(selectedTest.id);
    }, 3000);
    
    return () => clearInterval(intervalId);
  }, [selectedTest, executions, activeTab]);
  
  // Trigger search when searchQuery or selectedProjectFilter changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadFlowTests(false); // Reset to first page on search/filter change
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, selectedProjectFilter]);
  
  // Auto-refresh running executions on Tests tab
  // P1 flood fix: depend on a stable test-ID signature (not flowTests array reference),
  // so the effect doesn't re-fire on every setFlowTests() call (which would immediately
  // trigger a burst of N requests — 1 per test — and cause the 500+ request flood).
  const flowTestsIdsSignature = flowTests.map((t) => t.id).join(',');
  useEffect(() => {
    if (activeTab !== 'tests' || flowTests.length === 0) return;

    // Load immediately
    loadRunningExecutions();

    // Then poll every 5 seconds
    const intervalId = setInterval(() => {
      loadRunningExecutions();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [activeTab, flowTestsIdsSignature]);

  // Test management functions
  const createFlowTest = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/tests`,
        testForm,
        { headers: getAuthHeaders() }
      );
      
      // Track Flow Test creation
      trackFlowTestCreation('manual', testForm.name);
      
      await loadFlowTests();
      setShowCreateModal(false);
      resetTestForm();
      
      // Show success message
      showSuccess('Flow Test created successfully!');
    } catch (error) {
      console.error('Failed to create Flow Test:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create Flow Test';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const executeFlowTest = async (testId) => {
    // Get fresh test data from backend to ensure we have latest environment_id
    try {
      debugLog('ENVIRONMENT DEBUG - executeFlowTest getting fresh test data...');
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/tests/${testId}`,
        { headers: getAuthHeaders() }
      );
      const freshTest = response.data;
      
      debugLog('ENVIRONMENT DEBUG - Fresh test data environment_id:', freshTest.environment_id);
      
      setExecutionTest(freshTest);
      
      // Use functional update to ensure clean state
      setExecutionConfig(prevConfig => {
        const newConfig = {
          browser: freshTest.configuration?.browser || 'chromium',
          device_type: freshTest.configuration?.device_type || 'desktop',
          device_name: freshTest.configuration?.device_name || null,
          environment_id: freshTest.environment_id || null,
          credential_profile_id: null,  // Reset credential profile for new execution config
          is_headless: prevConfig.is_headless,
          debug_mode: prevConfig.debug_mode,
          parallel_executions: 1,
          viewport_width: freshTest.configuration?.viewport_width || 1920,
          viewport_height: freshTest.configuration?.viewport_height || 1080
        };
        debugLog('ENVIRONMENT DEBUG - Setting executionConfig with fresh environment_id:', newConfig.environment_id);
        return newConfig;
      });
      
      setShowExecutionModal(true);
    } catch (error) {
      console.error('Error getting fresh test data:', error);
      // Fallback to existing logic if API call fails
      const test = flowTests.find(t => t.id === testId);
      if (test) {
        setExecutionTest(test);
        setExecutionConfig(prevConfig => ({
          browser: test.configuration?.browser || 'chromium',
          device_type: test.configuration?.device_type || 'desktop',
          device_name: test.configuration?.device_name || null,
          environment_id: test.environment_id || null,
          credential_profile_id: null,  // Reset credential profile for new execution config
          is_headless: prevConfig.is_headless,
          debug_mode: prevConfig.debug_mode,
          parallel_executions: 1,
          viewport_width: test.configuration?.viewport_width || 1920,
          viewport_height: test.configuration?.viewport_height || 1080
        }));
        setShowExecutionModal(true);
      }
    }
  };

  const quickRunTest = async (test, mode = 'default') => {
    if (!test) return;
    
    try {
      setLoading(true);
      
      // Get fresh test data to ensure we have latest configuration
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/tests/${test.id}`,
        { headers: getAuthHeaders() }
      );
      const freshTest = response.data;
      
      // Determine headless mode based on quick run option
      let isHeadless = true; // Default to headless
      let debugMode = false;
      
      if (mode === 'headless') {
        isHeadless = true;
        debugMode = false;
      } else if (mode === 'headed') {
        isHeadless = false;
        debugMode = false;
      } else if (mode === 'default') {
        // Use test's saved configuration
        isHeadless = freshTest.configuration?.is_headless !== undefined ? freshTest.configuration.is_headless : true;
        debugMode = freshTest.configuration?.debug_mode || false;
      }
      
      // Use test's saved configuration with mode override
      const executionConfig = {
        browser: freshTest.configuration?.browser || 'chromium',
        device_type: freshTest.configuration?.device_type || 'desktop',
        device_name: freshTest.configuration?.device_name || null,
        environment_id: freshTest.environment_id || null,
        is_headless: isHeadless,
        debug_mode: debugMode,
        parallel_executions: 1,
        viewport_width: freshTest.configuration?.viewport_width || 1920,
        viewport_height: freshTest.configuration?.viewport_height || 1080
      };

      // Execute the test with saved configuration
      const execResponse = await axios.post(
        `${BACKEND_URL}/api/flow-testing/tests/${test.id}/execute`,
        {
          configuration_override: executionConfig
        },
        { headers: getAuthHeaders() }
      );

      // Track execution
      trackFlowTestExecution(test.id, test.name);
      
      // Show success message with mode indicator
      const modeLabel = mode === 'headless' ? ' (Headless - Faster)' : mode === 'headed' ? ' (Headed - Visual)' : '';
      showSuccess(`Test execution started${modeLabel}! Check the Executions tab for results.`);
      
      // Refresh the executions list for this specific test
      await loadExecutions(test.id);
      
      // Set the selected test so executions are filtered correctly
      setSelectedTest(test);
      
      // Switch to executions tab
      setActiveTab('executions');

    } catch (error) {
      console.error('Quick run failed:', error);
      showError(error.response?.data?.detail || 'Failed to execute test');
    } finally {
      setLoading(false);
    }
  };

  // Cancel execution function
  const openCancelExecutionModal = (executionId) => {
    setExecutionToCancel(executionId);
    setShowCancelExecutionModal(true);
  };

  const cancelExecution = async () => {
    if (!executionToCancel) return;

    try {
      setLoading(true);
      
      const response = await axios.put(
        `${BACKEND_URL}/api/flow-testing/executions/${executionToCancel}/cancel`,
        {},
        { headers: getAuthHeaders() }
      );
      
      showSuccess('Execution cancelled successfully!');
      setShowCancelExecutionModal(false);
      setExecutionToCancel(null);
      
      // Small delay to ensure database update is completed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh executions to show updated status
      if (selectedTest) {
        await loadExecutions(selectedTest.id);
      }
      
    } catch (error) {
      console.error('Cancel execution failed:', error);
      showError(error.response?.data?.detail || 'Failed to cancel execution');
    } finally {
      setLoading(false);
    }
  };

  // Pause execution function
  const pauseExecution = async (executionId) => {
    try {
      setLoading(true);
      
      const response = await axios.put(
        `${BACKEND_URL}/api/flow-testing/executions/${executionId}/pause`,
        {},
        { headers: getAuthHeaders() }
      );
      
      showSuccess('Execution paused successfully!');
      
      // Small delay to ensure database update is completed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh executions to show updated status
      if (selectedTest) {
        await loadExecutions(selectedTest.id);
      }
      
    } catch (error) {
      console.error('Pause execution failed:', error);
      showError(error.response?.data?.detail || 'Failed to pause execution');
    } finally {
      setLoading(false);
    }
  };

  // Resume execution function
  const resumeExecution = async (executionId) => {
    try {
      setLoading(true);
      
      const response = await axios.put(
        `${BACKEND_URL}/api/flow-testing/executions/${executionId}/resume`,
        {},
        { headers: getAuthHeaders() }
      );
      
      showSuccess('Execution resumed successfully!');
      
      // Small delay to ensure database update is completed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh executions to show updated status
      if (selectedTest) {
        await loadExecutions(selectedTest.id);
      }
      
    } catch (error) {
      console.error('Resume execution failed:', error);
      showError(error.response?.data?.detail || 'Failed to resume execution');
    } finally {
      setLoading(false);
    }
  };

  const confirmExecution = async () => {
    if (!executionTest) return;

    try {
      setLoading(true);
      
      // First, save the execution configuration back to the test (including environment)
      const updatedTestData = {
        ...executionTest,
        environment_id: executionConfig.environment_id,
        configuration: {
          ...executionTest.configuration,
          browser: executionConfig.browser,
          device_type: executionConfig.device_type,
          device_name: executionConfig.device_name,
          viewport_width: executionConfig.viewport_width,
          viewport_height: executionConfig.viewport_height,
          is_headless: executionConfig.is_headless
        }
      };
      
      debugLog('ENVIRONMENT DEBUG - Saving test with environment_id:', executionConfig.environment_id);
      debugLog('ENVIRONMENT DEBUG - Updated test data:', updatedTestData);
      
      const saveResponse = await axios.put(
        `${BACKEND_URL}/api/flow-testing/tests/${executionTest.id}`,
        updatedTestData,
        { headers: getAuthHeaders() }
      );
      
      debugLog('ENVIRONMENT DEBUG - Backend save response:', saveResponse.data);
      debugLog('ENVIRONMENT DEBUG - Saved test environment_id:', saveResponse.data.environment_id);
      
      // Immediately update the local flowTests state to reflect the changes
      setFlowTests(prevTests => {
        const updatedTests = prevTests.map(test => 
          test.id === executionTest.id 
            ? { ...test, ...updatedTestData }
            : test
        );
        debugLog('ENVIRONMENT DEBUG - Updated local flowTests, test now has environment_id:', 
          updatedTests.find(t => t.id === executionTest.id)?.environment_id);
        return updatedTests;
      });
      
      // Then execute the test
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/tests/${executionTest.id}/execute`,
        {
          parallel_executions: executionConfig.parallel_executions,
          run_in_debug_mode: executionConfig.debug_mode,
          environment_id: executionConfig.environment_id,  // Include environment in execution
          credential_profile_id: executionConfig.credential_profile_id,  // Include credential profile
          configuration_override: {
            browser: executionConfig.browser,
            device_type: executionConfig.device_type,
            device_name: executionConfig.device_name,
            viewport_width: executionConfig.viewport_width,
            viewport_height: executionConfig.viewport_height,
            is_headless: executionConfig.is_headless,
            enable_screenshots: true,
            timeout: 30000
          }
        },
        { headers: getAuthHeaders() }
      );
      
      const execution = response.data;
      
      // Store execution ID for better error tracking
      setLastExecutionId(execution.id);
      
      // Track Flow Test execution
      trackFlowTestExecution(executionTest.id, 'started');
      
      showSuccess(`Flow Test execution started! 
Execution ID: ${execution.id}
Status: ${execution.status}
Browser: ${execution?.browser || 'Unknown'}
Device: ${execution?.device_type || 'Unknown'}

Check the Executions tab for real-time progress and results.`);
      
      setShowExecutionModal(false);
      setExecutionTest(null);
      
      // Refresh data to show updated status
      await loadFlowTests();
      
      // If a test is selected, refresh its executions
      if (selectedTest && selectedTest.id === executionTest.id) {
        await loadExecutions(executionTest.id);
      }
    } catch (error) {
      console.error('Flow Test execution failed:', error);
      const errorDetails = error.response?.data?.detail || error.message || 'Unknown error occurred';
      showSuccess(`Flow Test execution failed!
${lastExecutionId ? `Last Execution ID: ${lastExecutionId}` : ''}
Error: ${errorDetails}

Please check the console for more details.`);
    } finally {
      setLoading(false);
    }
  };

  const regenerateFlowTestSteps = async (testId) => {
    try {
      setRegenerating(true);
      const response = await axios.put(
        `${BACKEND_URL}/api/flow-testing/tests/${testId}/regenerate`,
        {},
        { headers: getAuthHeaders() }
      );
      
      const updatedTest = response.data;
      
      // Update the execution test if it's the one being regenerated
      if (executionTest && executionTest.id === testId) {
        setExecutionTest(updatedTest);
      }
      
      // Refresh flow tests list
      await loadFlowTests();
      
      showSuccess(`Flow Test steps regenerated successfully using latest AI improvements!

Updated Test: ${updatedTest.name}
Regeneration Count: ${updatedTest.regenerated_count || 1}
New Steps: ${updatedTest.steps?.length || 0}

The test now uses the latest URL parsing fix and other AI improvements.`);
      
    } catch (error) {
      console.error('Failed to regenerate Flow Test steps:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to regenerate Flow Test steps';
      showError(`Regeneration failed: ${errorMessage}`);
    } finally {
      setRegenerating(false);
    }
  };

  const openDeleteModal = (testId) => {
    const test = flowTests.find(t => t.id === testId);
    setTestToDelete(test);
    setShowDeleteModal(true);
  };

  const deleteFlowTest = async () => {
    if (!testToDelete) return;

    try {
      setLoading(true);
      await axios.delete(
        `${BACKEND_URL}/api/flow-testing/tests/${testToDelete.id}`,
        { headers: getAuthHeaders() }
      );
      
      await loadFlowTests();
      if (selectedTest && selectedTest.id === testToDelete.id) {
        setSelectedTest(null);
      }
      
      showSuccess('Flow Test deleted successfully!');
      setShowDeleteModal(false);
      setTestToDelete(null);
    } catch (error) {
      console.error('Failed to delete Flow Test:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete Flow Test';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // AI Generation
  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      showWarning('Please enter a description for the Flow Test');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/generate`,
        {
          prompt: aiPrompt,
          environment_id: aiEnvironmentId || null
        },
        { headers: getAuthHeaders() }
      );
      
      // Track AI-generated Flow Test creation
      trackFlowTestCreation('ai_generated', response.data.name || aiPrompt.substring(0, 50));
      
      await loadFlowTests();
      setShowAIGenerateModal(false);
      setAiPrompt('');
      setAiEnvironmentId('');
      
      showSuccess('Flow Test generated successfully with AI!');
    } catch (error) {
      console.error('AI generation failed:', error);
      const errorDetails = error.response?.data?.detail || error.message || 'Unknown error occurred';
      showError(`AI Test Generation Failed!
Error: ${errorDetails}

Please check the console for more details.`);
    } finally {
      setLoading(false);
    }
  };

  // Environment management functions
  const openEditEnvironment = (environment) => {
    setEditingEnvironment(environment);
    setEnvironmentForm({
      name: environment.name,
      description: environment.description,
      base_url: environment.base_url,
      variables: environment.variables || {},
      credential_profiles: environment.credential_profiles || []
    });
    setEnvironmentVariablesText(JSON.stringify(environment.variables || {}, null, 2));
    // Load existing credential profiles
    setCredentialProfiles(environment.credential_profiles || []);
    setShowAddProfileForm(false);
    setNewProfileForm({ name: '', description: '', cookies: '', headers: '', is_default: false });
    setShowEditEnvironmentModal(true);
  };

  const updateEnvironment = async () => {
    if (!editingEnvironment) return;

    try {
      setLoading(true);
      
      // Validate JSON before sending
      let variables = {};
      try {
        if (environmentVariablesText.trim() === '') {
          variables = {};
        } else {
          variables = JSON.parse(environmentVariablesText);
        }
      } catch (err) {
        showWarning('Invalid JSON format in Variables field. Please check your syntax.');
        setLoading(false);
        return;
      }
      
      // Prepare update data with credential profiles
      const updateData = {
        name: environmentForm.name.trim(),
        description: environmentForm.description.trim(),
        base_url: environmentForm.base_url.trim(),
        variables,
        credential_profiles: credentialProfiles
      };
      
      await axios.put(
        `${BACKEND_URL}/api/flow-testing/environments/${editingEnvironment.id}`,
        updateData,
        { headers: getAuthHeaders() }
      );
      
      await loadEnvironments();
      setShowEditEnvironmentModal(false);
      setEditingEnvironment(null);
      resetEnvironmentForm();
      
      showSuccess('Environment updated successfully!');
    } catch (error) {
      console.error('Failed to update environment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update environment';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteEnvironmentModal = (environment) => {
    setEnvironmentToDelete(environment);
    setShowDeleteEnvironmentModal(true);
  };

  const deleteEnvironment = async () => {
    if (!environmentToDelete) return;

    try {
      setLoading(true);
      await axios.delete(
        `${BACKEND_URL}/api/flow-testing/environments/${environmentToDelete.id}`,
        { headers: getAuthHeaders() }
      );
      
      await loadEnvironments();
      showSuccess('Environment deleted successfully!');
      setShowDeleteEnvironmentModal(false);
      setEnvironmentToDelete(null);
    } catch (error) {
      console.error('Failed to delete environment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete environment';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Test Segments Management Functions
  const loadTestSegments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/flow-testing/segments`, {
        headers: getAuthHeaders()
      });
      setTestSegments(response.data);
    } catch (error) {
      console.error('Failed to load test segments:', error);
      showError('Failed to load test segments');
    }
  };

  const createTestSegment = async (segmentData) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/segments`,
        segmentData,
        { headers: getAuthHeaders() }
      );
      
      await loadTestSegments();
      setShowCreateSegmentModal(false);
      
      showSuccess(`Test segment "${segmentData.name}" created successfully!`);
    } catch (error) {
      console.error('Failed to create test segment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create test segment';
      showError(`${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const updateTestSegment = async (segmentId, updateData) => {
    try {
      setLoading(true);
      await axios.put(
        `${BACKEND_URL}/api/flow-testing/segments/${segmentId}`,
        updateData,
        { headers: getAuthHeaders() }
      );
      
      await loadTestSegments();
      setShowSegmentModal(false);
      setEditingSegment(null);
      
      showSuccess('Test segment updated successfully!');
    } catch (error) {
      console.error('Failed to update test segment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update test segment';
      showError(`${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteSegmentModal = (segment) => {
    setSegmentToDelete(segment);
    setShowDeleteSegmentModal(true);
  };

  const deleteTestSegment = async () => {
    if (!segmentToDelete) return;

    try {
      setLoading(true);
      await axios.delete(
        `${BACKEND_URL}/api/flow-testing/segments/${segmentToDelete.id}`,
        { headers: getAuthHeaders() }
      );
      
      await loadTestSegments();
      showSuccess('Test segment deleted successfully!');
      setShowDeleteSegmentModal(false);
      setSegmentToDelete(null);
    } catch (error) {
      console.error('Failed to delete test segment:', error);
      showError('Failed to delete test segment');
    } finally {
      setLoading(false);
    }
  };

  const insertTestSegment = async (testId, segmentId, position) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/tests/${testId}/insert-segment`,
        { segment_id: segmentId, position: position },
        { headers: getAuthHeaders() }
      );
      
      // Reload the specific test to get updated steps
      const updatedTestResponse = await axios.get(
        `${BACKEND_URL}/api/flow-testing/tests/${testId}`,
        { headers: getAuthHeaders() }
      );
      
      // Update editingTest with the new steps
      if (editingTest && editingTest.id === testId) {
        setEditingTest(updatedTestResponse.data);
        // Also update testForm to keep it in sync
        setTestForm(prev => ({
          ...prev,
          steps: updatedTestResponse.data.steps || []
        }));
      }
      
      // Reload the flow tests list
      await loadFlowTests();
      setShowInsertSegmentModal(false);
      
      showSuccess(`${response.data.message}`);
    } catch (error) {
      console.error('Failed to insert test segment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to insert test segment';
      showError(`${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const createSegmentFromSteps = async (testId, stepIndices, segmentData) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/segments/from-steps`,
        {
          test_id: testId,
          step_indices: stepIndices,
          segment_name: segmentData.name,
          segment_description: segmentData.description,
          segment_type: segmentData.segment_type
        },
        { headers: getAuthHeaders() }
      );
      
      await loadTestSegments();
      setSelectedStepsForSegment([]);
      
      showSuccess(`${response.data.message}`);
    } catch (error) {
      console.error('Failed to create segment from steps:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create segment from steps';
      showError(`${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const createEnvironment = async () => {
    try {
      setLoading(true);
      
      // Validate JSON before sending
      let variables = {};
      
      try {
        if (environmentVariablesText.trim() === '') {
          variables = {};
        } else {
          variables = JSON.parse(environmentVariablesText);
        }
      } catch (err) {
        showWarning('Invalid JSON format in Variables field. Please check your syntax.');
        setLoading(false);
        return;
      }
      
      // Prepare environment data with credential profiles
      const environmentData = {
        name: environmentForm.name.trim(),
        description: environmentForm.description.trim(),
        base_url: environmentForm.base_url.trim(),
        variables,
        credential_profiles: credentialProfiles
      };
      
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/environments`,
        environmentData,
        { headers: getAuthHeaders() }
      );
      
      await loadEnvironments();
      setShowEnvironmentModal(false);
      resetEnvironmentForm();
      
      showSuccess('Environment created successfully!');
    } catch (error) {
      console.error('Failed to create environment:', error);
      
      // Better error handling
      let errorMessage = 'Failed to create environment';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showError(`Environment Creation Failed\n\n${errorMessage}\n\nPlease check your input and try again.`);
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [showFullPageEditor, setShowFullPageEditor] = useState(false);

  // Edit test function - now opens full page editor
  const openEditTest = async (test) => {
    try {
      // Always fetch the latest test data before editing
      const response = await axios.get(
        `${BACKEND_URL}/api/flow-testing/tests/${test.id}`,
        { headers: getAuthHeaders() }
      );
      const latestTest = response.data;
      
      setEditingTest(latestTest);
      setTestForm({
        name: latestTest.name || '',
        description: latestTest.description || '',
        tags: latestTest.tags || [],
        steps: latestTest.steps || [],
        global_assertions: latestTest.global_assertions || [],
        configuration: {
          browser: latestTest.configuration?.browser || 'chromium',
          device_type: latestTest.configuration?.device_type || 'desktop',
          viewport_width: latestTest.configuration?.viewport_width || 1920,
          viewport_height: latestTest.configuration?.viewport_height || 1080,
          is_headless: latestTest.configuration?.is_headless !== undefined ? latestTest.configuration.is_headless : true,
          enable_screenshots: latestTest.configuration?.enable_screenshots !== undefined ? latestTest.configuration.enable_screenshots : true,
          enable_video: latestTest.configuration?.enable_video !== undefined ? latestTest.configuration.enable_video : false,
          timeout: latestTest.configuration?.timeout || 30000
        },
        environment_id: latestTest.environment_id || null,
        project_id: latestTest.project_id || null,  // Include project_id for editing
        test_variables: latestTest.test_variables || {},
        sensitive_variables: latestTest.sensitive_variables || []
      });
      // Open full-page editor instead of modal
      setShowFullPageEditor(true);
      
      debugLog('Opened full-page editor for test:', latestTest.name);
      
    } catch (error) {
      console.error('Failed to fetch latest test data for editing:', error);
      
      // Fallback to using the passed test data
      setEditingTest(test);
      setTestForm({
        name: test.name || '',
        description: test.description || '',
        tags: test.tags || [],
        steps: test.steps || [],
        global_assertions: test.global_assertions || [],
        configuration: {
          browser: test.configuration?.browser || 'chromium',
          device_type: test.configuration?.device_type || 'desktop',
          viewport_width: test.configuration?.viewport_width || 1920,
          viewport_height: test.configuration?.viewport_height || 1080,
          is_headless: test.configuration?.is_headless !== undefined ? test.configuration.is_headless : true,
          enable_screenshots: test.configuration?.enable_screenshots !== undefined ? test.configuration.enable_screenshots : true,
          enable_video: test.configuration?.enable_video !== undefined ? test.configuration.enable_video : false,
          timeout: test.configuration?.timeout || 30000
        },
        environment_id: test.environment_id || null,
        project_id: test.project_id || null,  // Include project_id for editing
        test_variables: test.test_variables || {},
        sensitive_variables: test.sensitive_variables || []
      });
      // Open full-page editor instead of modal
      setShowFullPageEditor(true);
      
      if (error.response?.status === 401) {
        showError('Session expired. Please refresh the page and login again.');
      } else {
        showWarning('Warning: Could not fetch latest test data. Using current data for editing.');
      }
    }
  };

  const updateFlowTest = async () => {
    if (!editingTest) return;

    try {
      setLoading(true);
      
      // Log the update attempt for debugging
      debugLog('Updating Flow Test:', editingTest.id, testForm);
      
      const response = await axios.put(
        `${BACKEND_URL}/api/flow-testing/tests/${editingTest.id}`,
        testForm,
        { headers: getAuthHeaders() }
      );
      
      // Log successful response
      debugLog('Flow Test update response:', response.data);
      
      // Force refresh of Flow Tests list with small delay to ensure backend completion
      setTimeout(async () => {
        await loadFlowTests();
      }, 500);
      
      // Clear edit state
      setShowEditModal(false);
      setEditingTest(null);
      resetTestForm();
      
      // Show success message
      showSuccess(`Flow Test "${response.data.name}" updated successfully! Changes have been saved.`);
      
    } catch (error) {
      console.error('Failed to update Flow Test:', error);
      
      // Enhanced error handling
      let errorMessage = 'Failed to update Flow Test';
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please refresh the page and login again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Flow Test not found. It may have been deleted by another user.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showError(`Update Failed: ${errorMessage}`);
      
      // If it's an auth error, refresh the page
      if (error.response?.status === 401) {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const resetTestForm = () => {
    setTestForm({
      name: '',
      description: '',
      tags: [],
      steps: [],
      global_assertions: [],
      configuration: {
        browser: 'chromium',
        device_type: 'desktop',
        viewport_width: 1920,
        viewport_height: 1080,
        is_headless: true,
        enable_screenshots: true,
        enable_video: false,
        timeout: 30000
      },
      environment_id: null,
      test_variables: {},
      sensitive_variables: []
    });
  };

  // Test Variables Management Functions
  const addOrUpdateVariable = () => {
    if (!newVariableName.trim()) {
      showError('Variable name is required');
      return;
    }

    // Validate variable name (alphanumeric and underscore only)
    if (!/^[a-zA-Z0-9_]+$/.test(newVariableName)) {
      showError('Variable name can only contain letters, numbers, and underscores');
      return;
    }

    const updatedVariables = { ...testForm.test_variables };
    updatedVariables[newVariableName] = newVariableValue;

    const updatedSensitiveVars = [...testForm.sensitive_variables];
    if (newVariableIsSensitive && !updatedSensitiveVars.includes(newVariableName)) {
      updatedSensitiveVars.push(newVariableName);
    } else if (!newVariableIsSensitive && updatedSensitiveVars.includes(newVariableName)) {
      const index = updatedSensitiveVars.indexOf(newVariableName);
      updatedSensitiveVars.splice(index, 1);
    }

    setTestForm({
      ...testForm,
      test_variables: updatedVariables,
      sensitive_variables: updatedSensitiveVars
    });

    // Reset form
    setNewVariableName('');
    setNewVariableValue('');
    setNewVariableIsSensitive(false);
    setEditingVariable(null);

    showSuccess(`Variable "${newVariableName}" ${editingVariable ? 'updated' : 'added'} successfully`);
  };

  const deleteVariable = (varName) => {
    const updatedVariables = { ...testForm.test_variables };
    delete updatedVariables[varName];

    const updatedSensitiveVars = testForm.sensitive_variables.filter(v => v !== varName);

    setTestForm({
      ...testForm,
      test_variables: updatedVariables,
      sensitive_variables: updatedSensitiveVars
    });

    showSuccess(`Variable "${varName}" deleted successfully`);
  };

  const editVariable = (varName) => {
    setEditingVariable(varName);
    setNewVariableName(varName);
    setNewVariableValue(testForm.test_variables[varName]);
    setNewVariableIsSensitive(testForm.sensitive_variables.includes(varName));
  };

  const saveValueAsVariable = (value, suggestedName = '') => {
    setNewVariableValue(value);
    setNewVariableName(suggestedName);
    setShowVariablesModal(true);
  };

  const maskSensitiveValue = (value) => {
    if (!value) return value;
    const len = value.length;
    if (len <= 2) return '•'.repeat(len);
    if (len <= 8) return value[0] + '•'.repeat(len - 2) + value[len - 1];
    return value.substring(0, 2) + '•'.repeat(len - 4) + value.substring(len - 2);
  };

  const analyzeVariableUsage = () => {
    const usage = {};
    const variables = testForm.test_variables || {};

    Object.keys(variables).forEach(varName => {
      usage[varName] = {
        count: 0,
        steps: []
      };
    });

    (testForm.steps || []).forEach((step, index) => {
      const stepValue = step.value || '';
      Object.keys(variables).forEach(varName => {
        if (stepValue.includes(`{{${varName}}}`)) {
          usage[varName].count++;
          usage[varName].steps.push(index + 1);
        }
      });
    });

    setVariableUsageMap(usage);
    return usage;
  };

  // Screenshot component that handles authenticated loading
  const Screenshot = ({ screenshotPath, alt, className, onClick }) => {
    const { screenshotUrl, loading } = useScreenshot(screenshotPath);
    
    if (loading) {
      return (
        <div className={`bg-gray-100 flex items-center justify-center ${className}`}>
          <div className="text-gray-500 text-xs">Loading...</div>
        </div>
      );
    }
    
    if (!screenshotUrl) {
      return (
        <div className={`bg-gray-100 flex items-center justify-center ${className}`}>
          <div className="text-gray-500 text-xs">No image</div>
        </div>
      );
    }
    
    return (
      <img
        src={screenshotUrl}
        alt={alt}
        className={className}
        onClick={onClick}
      />
    );
  };

  // Screenshot loading hook
  const useScreenshot = (screenshotPath) => {
    const [screenshotUrl, setScreenshotUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
      if (!screenshotPath) return;
      
      const filename = screenshotPath.split('/').pop();
      
      // Check cache first
      if (screenshotCache[filename]) {
        setScreenshotUrl(screenshotCache[filename]);
        return;
      }
      
      // Load screenshot
      setLoading(true);
      const loadScreenshot = async () => {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/flow-testing/screenshot-data/${filename}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          const dataUrl = response.data.data;
          setScreenshotUrl(dataUrl);
          
          // Update cache
          setScreenshotCache(prev => ({
            ...prev,
            [filename]: dataUrl
          }));
        } catch (error) {
          console.error('Failed to load screenshot:', error);
          setScreenshotUrl(null);
        } finally {
          setLoading(false);
        }
      };
      
      loadScreenshot();
    }, [screenshotPath]);
    
    return { screenshotUrl, loading };
  };

  const openScreenshotInNewTab = async (screenshotPath) => {
    try {
      if (!screenshotPath) {
        console.error('No screenshot path provided');
        return;
      }
      
      // Extract filename from path
      const filename = screenshotPath.split('/').pop();
      
      // Get screenshot data through authenticated API
      const response = await axios.get(`${BACKEND_URL}/api/flow-testing/screenshot-data/${filename}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const dataUrl = response.data.data;
      
      if (dataUrl && dataUrl.startsWith('data:image/')) {
        // Create a new window with the image
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>Screenshot - ${filename}</title>
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  background: #f0f0f0; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh; 
                  font-family: Arial, sans-serif;
                }
                img { 
                  max-width: 100%; 
                  max-height: 100vh; 
                  border: 1px solid #ccc; 
                  border-radius: 8px; 
                  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                  background: white;
                }
                .info {
                  position: absolute;
                  top: 10px;
                  left: 10px;
                  background: rgba(0,0,0,0.7);
                  color: white;
                  padding: 8px 12px;
                  border-radius: 4px;
                  font-size: 12px;
                }
              </style>
            </head>
            <body>
              <div class="info">Flow Test Screenshot: ${filename}</div>
              <img src="${dataUrl}" alt="Flow Test Screenshot" />
            </body>
            </html>
          `);
          newWindow.document.close();
        } else {
          // Fallback: try to open data URL directly
          window.open(dataUrl, '_blank');
        }
      } else {
        console.error('Invalid data URL received:', dataUrl?.substring(0, 50));
        showError('Failed to load screenshot. Please try again.');
      }
    } catch (error) {
      console.error('Failed to open screenshot:', error);
      showError('Failed to load screenshot. Please check your connection and try again.');
    }
  };

  const getScreenshotUrl = async (screenshotPath) => {
    if (!screenshotPath) return null;
    
    // Extract filename from path
    const filename = screenshotPath.split('/').pop();
    
    try {
      // Get screenshot data through authenticated API
      const response = await axios.get(`${BACKEND_URL}/api/flow-testing/screenshot-data/${filename}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      return response.data.data; // This is the base64 data URL
    } catch (error) {
      console.error('Failed to load screenshot:', error);
      return null;
    }
  };

  // Cache for screenshot URLs to avoid repeated API calls
  const [screenshotCache, setScreenshotCache] = useState({});

  const resetEnvironmentForm = () => {
    setEnvironmentForm({
      name: '',
      description: '',
      base_url: '',
      variables: {},
      credential_profiles: []
    });
    setEnvironmentVariablesText('');
    setCredentialProfiles([]);
    setShowAddProfileForm(false);
    setEditingProfileIndex(null);
    setNewProfileForm({
      name: '',
      description: '',
      cookies: '',
      headers: '',
      is_default: false
    });
  };

  // Step Management Utility Functions
  const convertStepToHumanReadable = (step) => {
    const action = step.action?.toLowerCase() || '';
    const value = step.value || '';
    const selector = step.selector?.value || '';
    const description = step.description || '';

    // If step already has a good description, use it
    if (description && !description.includes('selector') && !description.includes('action')) {
      return description;
    }

    // Convert based on action type
    switch (action) {
      case 'navigate':
        // Show actual URL or indicate if using environment variable
        const displayUrl = value.includes('{{') 
          ? `${value} (from environment)` 
          : value;
        return `📍 Navigate to: ${displayUrl}`;
      case 'click':
        return `👆 Click on: ${getElementDescription(selector)}`;
      case 'type':
        return `⌨️ Type "${value}" in: ${getElementDescription(selector)}`;
      case 'select':
        return `📋 Select "${value}" from: ${getElementDescription(selector)}`;
      case 'wait':
        return `⏳ Wait ${value}ms`;
      case 'screenshot':
        return `📸 Take screenshot${description ? ': ' + description : ''}`;
      case 'scroll':
        return `📜 Scroll to: ${getElementDescription(selector) || 'page'}`;
      case 'hover':
        return `🎯 Hover over: ${getElementDescription(selector)}`;
      default:
        return description || `${action.charAt(0).toUpperCase() + action.slice(1)}: ${value}`;
    }
  };

  const getElementDescription = (selector) => {
    if (!selector) return 'element';
    
    // Extract meaningful descriptions from selectors
    if (selector.includes('button') || selector.includes('btn')) return 'button';
    if (selector.includes('input')) return 'input field';
    if (selector.includes('select') || selector.includes('dropdown')) return 'dropdown';
    if (selector.includes('link') || selector.includes('a[')) return 'link';
    if (selector.includes('submit')) return 'submit button';
    if (selector.includes('email')) return 'email field';
    if (selector.includes('password')) return 'password field';
    if (selector.includes('search')) return 'search box';
    
    // Extract text content or IDs
    const textMatch = selector.match(/text\(['"]([^'"]+)['"]\)/);
    if (textMatch) return `"${textMatch[1]}"`;
    
    const idMatch = selector.match(/#([a-zA-Z][\w-]*)/);
    if (idMatch) return `#${idMatch[1]}`;
    
    return 'element';
  };

  const addStepFromPrompt = async () => {
    if (!stepPrompt.trim() || addingStep) return;

    try {
      setAddingStep(true);
      
      // Call AI to convert natural language to step
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/convert-prompt-to-step`,
        {
          prompt: stepPrompt.trim(),
          existing_steps: testForm.steps
        },
        { headers: getAuthHeaders() }
      );

      const newStep = response.data;
      
      // Add step to current test form
      setTestForm(prev => ({
        ...prev,
        steps: [...prev.steps, { ...newStep, order: prev.steps.length + 1 }]
      }));
      
      // Clear prompt
      setStepPrompt('');
      
    } catch (error) {
      console.error('Failed to convert prompt to step:', error);
      showError('Failed to convert prompt to step. Please try a different description.');
    } finally {
      setAddingStep(false);
    }
  };

  const addStepToSegment = async () => {
    if (!stepPrompt.trim() || addingStep || !editingSegment) return;

    try {
      setAddingStep(true);
      
      // Call AI to convert natural language to step
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/convert-prompt-to-step`,
        {
          prompt: stepPrompt.trim(),
          existing_steps: editingSegment.steps || []
        },
        { headers: getAuthHeaders() }
      );

      const newStep = response.data;
      
      // Add step to editing segment
      setEditingSegment(prev => ({
        ...prev,
        steps: [...(prev.steps || []), { ...newStep, order: (prev.steps?.length || 0) + 1 }]
      }));
      
      // Clear prompt
      setStepPrompt('');
      
    } catch (error) {
      console.error('Failed to convert prompt to step:', error);
      showError('Failed to convert prompt to step. Please try a different description.');
    } finally {
      setAddingStep(false);
    }
  };

  const openDeleteStepModal = (stepIndex) => {
    setStepToDelete(stepIndex);
    setShowDeleteStepModal(true);
  };

  const deleteStep = () => {
    if (stepToDelete === null) return;
    
    setTestForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, index) => index !== stepToDelete)
        .map((step, index) => ({ ...step, order: index + 1 }))
    }));
    
    showSuccess('Step deleted successfully!');
    setShowDeleteStepModal(false);
    setStepToDelete(null);
  };

  // Copy step function
  const copyStep = (index) => {
    const stepToCopy = testForm.steps[index];
    
    // Create a new step with copied data but new ID and order
    const copiedStep = {
      ...stepToCopy,
      id: `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Generate new unique ID
      order: testForm.steps.length + 1, // Place at end
      description: `${stepToCopy.description} (Copy)` // Add "(Copy)" to description for clarity
    };
    
    // Add the copied step to the end of the steps array
    const updatedSteps = [...testForm.steps, copiedStep];
    
    setTestForm({
      ...testForm,
      steps: updatedSteps
    });
  };

  const moveStep = (fromIndex, toIndex) => {
    setTestForm(prev => {
      const newSteps = [...prev.steps];
      const [movedStep] = newSteps.splice(fromIndex, 1);
      newSteps.splice(toIndex, 0, movedStep);
      
      // Update order numbers
      return {
        ...prev,
        steps: newSteps.map((step, index) => ({ ...step, order: index + 1 }))
      };
    });
  };

  const openSelectorEditor = (stepIndex) => {
    const step = testForm.steps[stepIndex];
    setEditingStepIndex(stepIndex);
    
    // Initialize selector form with current step's selector and description
    if (step.selector) {
      setSelectorForm({
        type: step.selector.type || 'css',
        value: step.selector.value || '',
        fallback_selectors: step.selector.fallback_selectors || [],
        stepTitle: step.description || '',
        timeout: step.timeout || 30000 // Default 30 seconds (better for navigation reliability)
      });
    } else {
      setSelectorForm({
        type: 'css',
        value: '',
        fallback_selectors: [],
        stepTitle: step.description || '',
        timeout: step.timeout || 30000 // Default 30 seconds (better for navigation reliability)
      });
    }
    
    setShowSelectorModal(true);
  };

  const updateStepSelector = () => {
    if (editingStepIndex === null) return;
    
    // Use the current testForm state, not prev, because navigation URL is edited directly
    const updatedSteps = testForm.steps.map((step, index) => {
      if (index === editingStepIndex) {
        // For navigation steps, preserve the value (URL) which was edited directly
        if (step.action === 'navigate') {
          return {
            ...step,
            description: selectorForm.stepTitle.trim() || step.description,
            timeout: selectorForm.timeout || 30000, // Default 30 seconds for navigation
            // value (URL) is already updated in testForm.steps from direct editing
          };
        }
        
        // For other steps, update selector and timeout
        return {
          ...step,
          description: selectorForm.stepTitle.trim() || step.description,
          timeout: selectorForm.timeout || 8000, // Ensure timeout is set
          selector: {
            type: selectorForm.type,
            value: selectorForm.value,
            fallback_selectors: selectorForm.fallback_selectors.filter(fs => fs.value.trim())
          }
        };
      }
      return step;
    });
    
    setTestForm(prev => ({
      ...prev,
      steps: updatedSteps
    }));
    
    setShowSelectorModal(false);
    setEditingStepIndex(null);
  };

  const addFallbackSelector = () => {
    setSelectorForm(prev => ({
      ...prev,
      fallback_selectors: [...prev.fallback_selectors, { type: 'css', value: '' }]
    }));
  };

  const updateFallbackSelector = (index, field, value) => {
    setSelectorForm(prev => ({
      ...prev,
      fallback_selectors: prev.fallback_selectors.map((fs, i) => 
        i === index ? { ...fs, [field]: value } : fs
      )
    }));
  };

  const removeFallbackSelector = (index) => {
    setSelectorForm(prev => ({
      ...prev,
      fallback_selectors: prev.fallback_selectors.filter((_, i) => i !== index)
    }));
  };

  // Utility functions
  const formatLastRunDateTime = (isoString) => {
    if (!isoString) return 'Never';
    
    try {
      const date = new Date(isoString);
      
      // Convert to EST (UTC-5) or EDT (UTC-4) - let JavaScript handle DST
      const options = {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      
      return date.toLocaleString('en-US', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'error': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'running': return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'pending': return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'paused': return <Clock className="h-4 w-4 text-orange-600" />;
      case 'cancelled': return <XCircle className="h-4 w-4 text-gray-600" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'running': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'paused': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getBrowserIcon = (browser) => {
    switch (browser) {
      case 'chromium': return <Chrome className="h-4 w-4" />;
      case 'firefox': return <Globe className="h-4 w-4" />;
      case 'webkit': return <Monitor className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      case 'desktop': return <Monitor className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  // Schedule functions
  const loadSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/schedules`, {
        headers: getAuthHeaders()
      });
      const schedulesData = response.data;
      
      // Filter to only show flow test schedules
      const flowSchedules = schedulesData.filter(schedule => schedule.test_type === 'flow_test');
      
      // Enhance schedules with execution details
      const enhancedSchedules = await Promise.all(
        flowSchedules.map(async (schedule) => {
          try {
            // Get the latest execution for this schedule
            const executionsResponse = await axios.get(
              `${BACKEND_URL}/api/schedules/${schedule.id}/executions`,
              { headers: getAuthHeaders() }
            );
            const executions = executionsResponse.data;
            
            if (executions && executions.length > 0) {
              // Get the most recent execution
              const latestExecution = executions[0];
              
              // Get execution number from the schedule execution record directly, 
              // or fallback to fetching from flow executions
              let executionNumber = latestExecution.execution_number || null;
              
              if (!executionNumber && latestExecution.execution_id && schedule.test_type === 'flow_test') {
                try {
                  const testExecutionsResponse = await axios.get(
                    `${BACKEND_URL}/api/flow-testing/tests/${schedule.test_id}/executions?limit=10`,
                    { headers: getAuthHeaders() }
                  );
                  // Handle paginated response - extract array from response
                  const testExecutions = Array.isArray(testExecutionsResponse.data) 
                    ? testExecutionsResponse.data 
                    : (testExecutionsResponse.data.executions || testExecutionsResponse.data || []);
                  const matchingExecution = testExecutions.find(exec => exec.id === latestExecution.execution_id);
                  if (matchingExecution) {
                    executionNumber = matchingExecution.execution_number;
                  }
                } catch (error) {
                  console.warn('Could not load execution number:', error);
                }
              }
              
              return {
                ...schedule,
                total_executions: (schedule.successful_executions || 0) + (schedule.failed_executions || 0),
                latestExecution: {
                  executed_at: latestExecution.completed_at || latestExecution.started_at,
                  status: latestExecution.result || latestExecution.status,
                  execution_id: latestExecution.execution_id,
                  executionNumber: executionNumber,
                  steps_passed: latestExecution.steps_passed,
                  steps_failed: latestExecution.steps_failed,
                  response_time: latestExecution.response_time
                }
              };
            }
            
            return {
              ...schedule,
              total_executions: (schedule.successful_executions || 0) + (schedule.failed_executions || 0)
            };
          } catch (error) {
            console.warn(`Could not load executions for schedule ${schedule.id}:`, error);
            return schedule;
          }
        })
      );
      
      setSchedules(enhancedSchedules);
    } catch (error) {
      console.error('Failed to load schedules:', error);
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const createSchedule = async () => {
    if (!selectedTestForSchedule) return;

    setLoading(true);
    try {
      const scheduleData = {
        test_id: selectedTestForSchedule.id,
        test_type: 'flow_test',
        schedule_type: scheduleForm.schedule_type,
        timezone: scheduleForm.timezone,
        email_notifications: scheduleForm.email_notifications,
        in_app_notifications: scheduleForm.in_app_notifications
      };

      if (scheduleForm.schedule_type === 'one_time') {
        if (!scheduleForm.scheduled_datetime) {
          showWarning('Please select a date and time for the scheduled execution');
          return;
        }
        scheduleData.scheduled_datetime = scheduleForm.scheduled_datetime;
      } else {
        if (!scheduleForm.start_date || !scheduleForm.time_of_day) {
          showWarning('Please select a start date and time for recurring execution');
          return;
        }
        scheduleData.recurrence_pattern = scheduleForm.recurrence_pattern;
        scheduleData.start_date = new Date(scheduleForm.start_date).toISOString();
        scheduleData.time_of_day = scheduleForm.time_of_day;
        
        if (scheduleForm.end_date) {
          scheduleData.end_date = new Date(scheduleForm.end_date).toISOString();
        }
      }

      await axios.post(`${BACKEND_URL}/api/schedules`, scheduleData, {
        headers: getAuthHeaders()
      });
      
      setShowScheduleModal(false);
      await loadSchedules();
      showSuccess('Schedule created successfully!');
      
      // Reset schedule form
      setScheduleForm({
        schedule_type: 'one_time',
        scheduled_datetime: '',
        recurrence_pattern: 'daily',
        start_date: '',
        end_date: '',
        time_of_day: '09:00',
        timezone: 'America/New_York',
        email_notifications: true,
        in_app_notifications: true
      });
      
    } catch (error) {
      console.error('Failed to create schedule:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create schedule';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleSchedule = async (scheduleId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/schedules/${scheduleId}/toggle`, {}, {
        headers: getAuthHeaders()
      });
      await loadSchedules();
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
      showError('Failed to toggle schedule status');
    }
  };

  const openDeleteScheduleModal = (schedule) => {
    setScheduleToDelete(schedule);
    setShowDeleteScheduleModal(true);
  };

  const deleteSchedule = async () => {
    if (!scheduleToDelete) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/schedules/${scheduleToDelete.id}`, {
        headers: getAuthHeaders()
      });
      await loadSchedules();
      showSuccess('Schedule deleted successfully');
      setShowDeleteScheduleModal(false);
      setScheduleToDelete(null);
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      showError('Failed to delete schedule');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <IDTooltip id="FLT-01">
            <h2 className="text-2xl font-bold">Flow Testing</h2>
          </IDTooltip>
          <p className="text-gray-600">Modern cross-browser UI testing with AI-powered test generation</p>
        </div>
        <div className="flex space-x-2">
          <Dialog open={showAIGenerateModal} onOpenChange={setShowAIGenerateModal}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                <Brain className="h-4 w-4 mr-2" />
                Generate with AI
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Generate Flow Test with AI</DialogTitle>
                <DialogDescription>
                  Describe your testing scenario and let AI generate the test steps for you.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Describe what you want to test
                  </label>
                  <Textarea
                    placeholder="Example: Login to the application, search for products, and verify search results are displayed"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    rows={4}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Environment (Optional)
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={aiEnvironmentId}
                    onChange={(e) => setAiEnvironmentId(e.target.value)}
                  >
                    <option value="">Select Environment</option>
                    {environments.map((env) => (
                      <option key={env.id} value={env.id}>
                        {env.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowAIGenerateModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={generateWithAI}
                    disabled={loading}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
                    Generate Test
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Create Live Test - Direct VNC Recorder */}
          <Button 
            className="bg-red-600 hover:bg-red-700"
            onClick={() => setShowVNCRecorder(true)}
          >
            <Play className="h-4 w-4 mr-2" />
            Create Live Test
          </Button>

          {/* Hidden: Iframe Recorder - keeping code for future use */}
          {/* 
          <Button 
            variant="outline"
            onClick={() => setShowLiveRecorder(true)}
          >
            <Globe className="h-4 w-4 mr-2" />
            Iframe Recorder (Legacy)
          </Button>
          */}

          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Test
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Flow Test</DialogTitle>
                <DialogDescription>
                  Create a new automated browser flow test for your application.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <Input
                    placeholder="Flow Test Name"
                    value={testForm.name}
                    onChange={(e) => setTestForm({...testForm, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Textarea
                    placeholder="Describe what this test does"
                    value={testForm.description}
                    onChange={(e) => setTestForm({...testForm, description: e.target.value})}
                    rows={3}
                  />
                </div>
                {/* Project Selector - Phase 2.2 */}
                {projects.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center">
                      <Folder className="h-4 w-4 mr-2 text-purple-600" />
                      Project (Optional)
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={testForm.project_id || ''}
                      onChange={(e) => setTestForm({...testForm, project_id: e.target.value || null})}
                    >
                      <option value="">No Project (Uncategorized)</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.icon} {project.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Organize your tests into projects</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-2">Website URL</label>
                  <Input
                    placeholder="https://example.com"
                    value={testForm.website_url}
                    onChange={(e) => setTestForm({...testForm, website_url: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Environment</label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={testForm.environment_id || ''}
                    onChange={(e) => setTestForm({...testForm, environment_id: e.target.value || null})}
                  >
                    <option value="">No Environment</option>
                    {environments.map((env) => (
                      <option key={env.id} value={env.id}>
                        {env.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={createFlowTest}
                    disabled={loading || !testForm.name.trim()}
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Create Test
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Execution Configuration Modal */}
      <Dialog open={showExecutionModal} onOpenChange={setShowExecutionModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-visible">
          <div className="overflow-y-auto max-h-[80vh]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Configure Test Execution</DialogTitle>
            <DialogDescription>
              Set up browser and execution options for your flow test.
            </DialogDescription>
          </DialogHeader>
          {executionTest && (
            <>
            <div className="flex-1 overflow-y-auto pr-2">
              <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Test: {executionTest.name}</h3>
                <p className="text-gray-600 mb-4">{executionTest.description}</p>
                
                {/* Regeneration Option for AI-Generated Tests */}
                {executionTest.generated_by_ai && executionTest.ai_prompt && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-blue-900 mb-1">
                          🤖 AI-Generated Test
                        </h4>
                        <p className="text-sm text-blue-700 mb-2">
                          Original prompt: "{executionTest.ai_prompt}"
                        </p>
                        {executionTest.regenerated_count && (
                          <p className="text-xs text-blue-600">
                            Regenerated {executionTest.regenerated_count} time(s)
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => regenerateFlowTestSteps(executionTest.id)}
                        disabled={regenerating || loading}
                        className="text-blue-600 border-blue-200 hover:bg-blue-100"
                      >
                        {regenerating ? (
                          <>
                            <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <Brain className="h-3 w-3 mr-1" />
                            Use Latest AI
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      ✨ Regenerate test steps using the latest AI improvements (like the new URL parsing fix)
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Browser</label>
                    <select
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={executionConfig.browser}
                      onChange={(e) => setExecutionConfig({...executionConfig, browser: e.target.value})}
                    >
                      <option value="chromium">Chromium (Chrome)</option>
                      <option value="firefox">Firefox</option>
                      <option value="webkit">WebKit (Safari)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-2">Cross-browser testing support</p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Device Type</label>
                    <select
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={executionConfig.device_type}
                      onChange={(e) => {
                        const deviceType = e.target.value;
                        setExecutionConfig({
                          ...executionConfig, 
                          device_type: deviceType,
                          device_name: null,
                          viewport_width: deviceType === 'desktop' ? 1920 : deviceType === 'tablet' ? 1024 : 390,
                          viewport_height: deviceType === 'desktop' ? 1080 : deviceType === 'tablet' ? 768 : 844
                        });
                      }}
                    >
                      <option value="desktop">Desktop</option>
                      <option value="tablet">Tablet</option>
                      <option value="mobile">Mobile</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Environment</label>
                    <select
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={executionConfig.environment_id || ''}
                      onChange={(e) => {
                        const selectedValue = e.target.value === '' ? null : e.target.value;
                        debugLog('ENVIRONMENT DROPDOWN - Selected value:', selectedValue);
                        setExecutionConfig(prevConfig => ({
                          ...prevConfig,
                          environment_id: selectedValue,
                          credential_profile_id: null  // Reset credential profile when environment changes
                        }));
                      }}
                    >
                      <option value="">No Environment</option>
                      {environments.map((env) => (
                        <option key={env.id} value={env.id}>
                          {env.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Credential Profile Selector - Show when environment has multiple profiles */}
                {executionConfig.environment_id && (() => {
                  const selectedEnv = environments.find(env => env.id === executionConfig.environment_id);
                  const profiles = selectedEnv?.credential_profiles || [];
                  if (profiles.length > 0) {
                    return (
                      <div className="space-y-2 p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <label className="block text-sm font-medium text-gray-700">
                          <User className="h-4 w-4 inline mr-1" />
                          Credential Profile
                        </label>
                        <select
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                          value={executionConfig.credential_profile_id || ''}
                          onChange={(e) => setExecutionConfig({
                            ...executionConfig, 
                            credential_profile_id: e.target.value === '' ? null : e.target.value
                          })}
                        >
                          <option value="">Use Default Profile</option>
                          {profiles.map((profile) => (
                            <option key={profile.id} value={profile.id}>
                              {profile.name} {profile.is_default ? '(Default)' : ''} - {profile.cookies?.length || 0} cookies
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-purple-600">
                          Select which user credentials to use for this test run
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {executionConfig.device_type === 'mobile' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Mobile Device</label>
                    <select
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={executionConfig.device_name || ''}
                      onChange={(e) => setExecutionConfig({...executionConfig, device_name: e.target.value || null})}
                    >
                      <option value="">Custom Mobile</option>
                      <optgroup label="📱 iPhone">
                        <option value="iPhone 14 Pro Max">iPhone 14 Pro Max (430x932)</option>
                        <option value="iPhone 14 Pro">iPhone 14 Pro (393x852)</option>
                        <option value="iPhone 14">iPhone 14 (390x844)</option>
                        <option value="iPhone 13 Pro">iPhone 13 Pro (390x844)</option>
                        <option value="iPhone 13">iPhone 13 (390x844)</option>
                        <option value="iPhone 12 Pro">iPhone 12 Pro (390x844)</option>
                        <option value="iPhone 12">iPhone 12 (390x844)</option>
                        <option value="iPhone SE">iPhone SE (375x667)</option>
                        <option value="iPhone 11">iPhone 11 (414x896)</option>
                      </optgroup>
                      <optgroup label="🤖 Samsung Galaxy">
                        <option value="Samsung Galaxy S23 Ultra">Galaxy S23 Ultra (384x824)</option>
                        <option value="Samsung Galaxy S23">Galaxy S23 (360x780)</option>
                        <option value="Samsung Galaxy S22">Galaxy S22 (360x780)</option>
                        <option value="Samsung Galaxy S21">Galaxy S21 (360x800)</option>
                        <option value="Samsung Galaxy A54">Galaxy A54 (412x915)</option>
                      </optgroup>
                      <optgroup label="📷 Google Pixel">
                        <option value="Pixel 7 Pro">Pixel 7 Pro (412x915)</option>
                        <option value="Pixel 7">Pixel 7 (412x915)</option>
                        <option value="Pixel 6">Pixel 6 (412x915)</option>
                        <option value="Pixel 5">Pixel 5 (393x851)</option>
                      </optgroup>
                      <optgroup label="🌐 Other Devices">
                        <option value="OnePlus 9 Pro">OnePlus 9 Pro (412x919)</option>
                        <option value="Xiaomi Redmi Note 11">Xiaomi Redmi Note 11 (393x873)</option>
                      </optgroup>
                    </select>
                  </div>
                )}

                {executionConfig.device_type === 'tablet' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Tablet Device</label>
                    <select
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent overflow-y-auto"
                      style={{ maxHeight: '300px' }}
                      value={executionConfig.device_name || ''}
                      onChange={(e) => setExecutionConfig({...executionConfig, device_name: e.target.value || null})}
                    >
                      <option value="">Custom Tablet</option>
                      <optgroup label="📱 iPad">
                        <option value="iPad Pro 12.9">iPad Pro 12.9" (1024x1366)</option>
                        <option value="iPad Pro 11">iPad Pro 11" (834x1194)</option>
                        <option value="iPad Air">iPad Air (820x1180)</option>
                        <option value="iPad Mini">iPad Mini (768x1024)</option>
                        <option value="iPad">iPad (810x1080)</option>
                      </optgroup>
                      <optgroup label="🤖 Samsung">
                        <option value="Galaxy Tab S8">Galaxy Tab S8 (800x1280)</option>
                        <option value="Galaxy Tab S7">Galaxy Tab S7 (753x1037)</option>
                      </optgroup>
                    </select>
                  </div>
                )}

                {executionConfig.device_type === 'desktop' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Desktop/Laptop Configuration</label>
                    <select
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={executionConfig.device_name || ''}
                      onChange={(e) => setExecutionConfig({...executionConfig, device_name: e.target.value || null})}
                    >
                      <option value="">Default Desktop (1920x1080)</option>
                      <optgroup label="💻 Desktop Resolutions">
                        <option value="Desktop 1920x1080">Full HD - 1920x1080 (Most Common)</option>
                        <option value="Desktop 2560x1440">2K/QHD - 2560x1440</option>
                        <option value="Desktop 1366x768">Laptop HD - 1366x768</option>
                        <option value="Laptop 1440x900">MacBook Air - 1440x900</option>
                      </optgroup>
                    </select>
                  </div>
                )}

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Execution Mode</label>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="headless"
                        checked={executionConfig.is_headless}
                        onChange={() => setExecutionConfig({...executionConfig, is_headless: true, debug_mode: false})}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Headless (Faster, background)</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="headless"
                        checked={!executionConfig.is_headless && !executionConfig.debug_mode}
                        onChange={() => setExecutionConfig({...executionConfig, is_headless: false, debug_mode: false})}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Headed (Visual, debugging)</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="headless"
                        checked={executionConfig.debug_mode}
                        onChange={() => setExecutionConfig({...executionConfig, is_headless: false, debug_mode: true})}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Debug Mode (Step-by-step with details)</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Parallel Executions</label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={executionConfig.parallel_executions}
                    onChange={(e) => setExecutionConfig({...executionConfig, parallel_executions: parseInt(e.target.value)})}
                  >
                    <option value={1}>1 (Sequential)</option>
                    <option value={2}>2 (Parallel)</option>
                    <option value={3}>3 (Max Parallel)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Viewport Width</label>
                  <Input
                    type="number"
                    value={executionConfig.viewport_width}
                    onChange={(e) => setExecutionConfig({...executionConfig, viewport_width: parseInt(e.target.value)})}
                    min="320"
                    max="3840"
                    className="p-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Viewport Height</label>
                  <Input
                    type="number"
                    value={executionConfig.viewport_height}
                    onChange={(e) => setExecutionConfig({...executionConfig, viewport_height: parseInt(e.target.value)})}
                    min="240"
                    max="2160"
                    className="p-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-md">
                <h4 className="font-semibold text-blue-800 mb-2">Execution Summary</h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>🌐 Browser: {executionConfig.browser} ({executionConfig.device_type})</p>
                  <p>📱 Device: {executionConfig.device_name || `Custom ${executionConfig.device_type}`}</p>
                  {executionConfig.environment_id ? (() => {
                    const selectedEnv = environments.find(env => env.id === executionConfig.environment_id);
                    return (
                      <p>🌍 Environment: {selectedEnv?.name || 'Unknown'} {selectedEnv?.variables && Object.keys(selectedEnv.variables).length > 0 && `(${Object.keys(selectedEnv.variables).length} vars)`}</p>
                    );
                  })() : <p>🌍 Environment: None</p>}
                  <p>👁️ Mode: {executionConfig.is_headless ? 'Headless' : 'Headed (Debug)'}</p>
                  <p>📏 Viewport: {executionConfig.viewport_width} × {executionConfig.viewport_height}</p>
                  <p>🔄 Parallel: {executionConfig.parallel_executions} execution(s)</p>
                </div>
              </div>
            </div>
            
            {/* Fixed Footer with Buttons - Outside scrollable area */}
            <div className="flex-shrink-0 border-t pt-4 mt-6 bg-white">
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowExecutionModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmExecution}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                  Execute Test
                </Button>
              </div>
            </div>
            </div>
            </>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Test Segment Modal */}
      <Dialog open={showCreateSegmentModal} onOpenChange={setShowCreateSegmentModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Test Segment</DialogTitle>
            <DialogDescription>
              Create a reusable test segment to share common flows across multiple tests.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Segment Name *</label>
                <Input
                  placeholder="e.g., User Login Flow"
                  value={segmentForm.name}
                  onChange={(e) => setSegmentForm({...segmentForm, name: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Segment Type</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={segmentForm.segment_type}
                  onChange={(e) => setSegmentForm({...segmentForm, segment_type: e.target.value})}
                >
                  <option value="login">Login Flow</option>
                  <option value="navigation">Navigation</option>
                  <option value="form_filling">Form Filling</option>
                  <option value="verification">Verification</option>
                  <option value="setup">Setup/Preparation</option>
                  <option value="teardown">Cleanup/Teardown</option>
                  <option value="generic">Generic</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                placeholder="Describe what this segment does and when to use it..."
                value={segmentForm.description}
                onChange={(e) => setSegmentForm({...segmentForm, description: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
              <p className="text-sm text-yellow-800">
                <strong>💡 Note:</strong> You'll add steps to this segment after creation, or you can create segments from existing test steps using the step selection feature in the Edit Test interface.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateSegmentModal(false);
                setSegmentForm({ name: '', description: '', segment_type: 'generic', tags: [] });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!segmentForm.name.trim()) {
                  showWarning('Please enter a segment name');
                  return;
                }
                
                createTestSegment({
                  ...segmentForm,
                  steps: [] // Start with empty steps, user can add them later
                });
                setSegmentForm({ name: '', description: '', segment_type: 'generic', tags: [] });
              }}
              disabled={loading || !segmentForm.name.trim()}
            >
              <Target className="h-4 w-4 mr-2" />
              Create Segment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Segment from Steps Modal */}
      <Dialog open={showCreateSegmentFromStepsModal} onOpenChange={setShowCreateSegmentFromStepsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Segment from Selected Steps</DialogTitle>
            <DialogDescription>
              Create a reusable segment from {selectedStepsForSegment.length} selected step(s).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Selected Steps Preview:</h4>
              <div className="space-y-1 text-sm">
                {selectedStepsForSegment.map((stepIndex, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Badge variant="secondary" className="text-xs">
                      {stepIndex + 1}
                    </Badge>
                    <span className="text-gray-700">
                      {testForm.steps[stepIndex] ? convertStepToHumanReadable(testForm.steps[stepIndex]) : 'Invalid step'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Segment Name *</label>
                <Input
                  placeholder="e.g., Login and Navigate to Dashboard"
                  value={segmentForm.name}
                  onChange={(e) => setSegmentForm({...segmentForm, name: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Segment Type</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={segmentForm.segment_type}
                  onChange={(e) => setSegmentForm({...segmentForm, segment_type: e.target.value})}
                >
                  <option value="login">Login Flow</option>
                  <option value="navigation">Navigation</option>
                  <option value="form_filling">Form Filling</option>
                  <option value="verification">Verification</option>
                  <option value="setup">Setup/Preparation</option>
                  <option value="teardown">Cleanup/Teardown</option>
                  <option value="generic">Generic</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                placeholder="Describe what this segment does and when to reuse it..."
                value={segmentForm.description}
                onChange={(e) => setSegmentForm({...segmentForm, description: e.target.value})}
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateSegmentFromStepsModal(false);
                setSegmentForm({ name: '', description: '', segment_type: 'generic', tags: [] });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!segmentForm.name.trim()) {
                  showWarning('Please enter a segment name');
                  return;
                }
                
                createSegmentFromSteps(editingTest.id, selectedStepsForSegment, segmentForm);
                setShowCreateSegmentFromStepsModal(false);
                setSegmentForm({ name: '', description: '', segment_type: 'generic', tags: [] });
              }}
              disabled={loading || !segmentForm.name.trim()}
            >
              <Target className="h-4 w-4 mr-2" />
              Create Segment from Steps
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Test Segment Modal */}
      <Dialog open={showSegmentModal} onOpenChange={setShowSegmentModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Test Segment</DialogTitle>
            <DialogDescription>
              Modify the segment details and steps.
            </DialogDescription>
          </DialogHeader>
          
          {editingSegment && (
            <div className="space-y-6">
              {/* Basic Segment Information */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Segment Name *</label>
                    <Input
                      placeholder="e.g., User Login Flow"
                      value={editingSegment.name || ''}
                      onChange={(e) => setEditingSegment({...editingSegment, name: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Segment Type</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={editingSegment.segment_type || 'generic'}
                      onChange={(e) => setEditingSegment({...editingSegment, segment_type: e.target.value})}
                    >
                      <option value="login">Login Flow</option>
                      <option value="navigation">Navigation</option>
                      <option value="form_filling">Form Filling</option>
                      <option value="verification">Verification</option>
                      <option value="setup">Setup/Preparation</option>
                      <option value="teardown">Cleanup/Teardown</option>
                      <option value="generic">Generic</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Textarea
                    placeholder="Describe what this segment does and when to use it..."
                    value={editingSegment.description || ''}
                    onChange={(e) => setEditingSegment({...editingSegment, description: e.target.value})}
                    rows={3}
                  />
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>📊 Used in {editingSegment.usage_count || 0} tests</span>
                  <span>🕒 Created {new Date(editingSegment.created_at).toLocaleDateString()}</span>
                  {editingSegment.updated_at && (
                    <span>✏️ Updated {new Date(editingSegment.updated_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>

              {/* Segment Steps Management */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="text-lg font-semibold">Segment Steps Management</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Steps ({editingSegment.steps?.length || 0})</h4>
                    <Badge variant="outline" className="text-xs">
                      Steps will be inserted as a group when used
                    </Badge>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-3 bg-gray-50">
                    {(!editingSegment.steps || editingSegment.steps.length === 0) ? (
                      <div className="text-center py-8 text-gray-500">
                        <Code className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">No steps in this segment</p>
                        <p className="text-xs">Add steps using natural language or copy from existing tests</p>
                      </div>
                    ) : (
                      editingSegment.steps.map((step, index) => (
                        <div key={index} className="p-3 bg-white rounded-lg border hover:shadow-sm transition-shadow">
                          <div className="flex items-start space-x-2">
                            <div className="flex flex-col space-y-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                                onClick={() => {
                                  const newSteps = [...editingSegment.steps];
                                  if (index > 0) {
                                    [newSteps[index], newSteps[index-1]] = [newSteps[index-1], newSteps[index]];
                                    // Update order numbers
                                    newSteps.forEach((s, i) => s.order = i + 1);
                                    setEditingSegment({...editingSegment, steps: newSteps});
                                  }
                                }}
                                disabled={index === 0}
                                title="Move up"
                              >
                                ↑
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                                onClick={() => {
                                  const newSteps = [...editingSegment.steps];
                                  if (index < newSteps.length - 1) {
                                    [newSteps[index], newSteps[index+1]] = [newSteps[index+1], newSteps[index]];
                                    // Update order numbers
                                    newSteps.forEach((s, i) => s.order = i + 1);
                                    setEditingSegment({...editingSegment, steps: newSteps});
                                  }
                                }}
                                disabled={index === editingSegment.steps.length - 1}
                                title="Move down"
                              >
                                ↓
                              </Button>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-2">
                                <Badge variant="secondary" className="text-xs font-mono">
                                  {index + 1}
                                </Badge>
                                <span className="text-sm font-medium text-gray-900">
                                  {convertStepToHumanReadable(step)}
                                </span>
                              </div>
                              
                              {step.selector && (
                                <div className="bg-gray-50 p-2 rounded text-xs space-y-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-gray-700">Web Element:</span>
                                  </div>
                                  <code className="text-xs bg-white px-2 py-1 rounded block break-all">
                                    {step.selector.type}: {step.selector.value}
                                  </code>
                                  {step.selector.fallback_selectors?.length > 0 && (
                                    <div className="text-xs text-gray-600">
                                      +{step.selector.fallback_selectors.length} fallback(s)
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {step.description && step.description !== convertStepToHumanReadable(step) && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {step.description}
                                </p>
                              )}
                            </div>
                            
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                              onClick={() => {
                                const newSteps = editingSegment.steps.filter((_, i) => i !== index);
                                // Update order numbers
                                newSteps.forEach((s, i) => s.order = i + 1);
                                setEditingSegment({...editingSegment, steps: newSteps});
                              }}
                              title="Delete step"
                            >
                              ✕
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Add Step to Segment */}
                  <div className="bg-blue-50 p-3 rounded-lg space-y-2">
                    <h5 className="font-medium text-blue-800 text-sm">Add Step to Segment</h5>
                    <div className="flex space-x-2">
                      <Input
                        placeholder="Describe step: click submit, type email, select option..."
                        value={stepPrompt}
                        onChange={(e) => setStepPrompt(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addStepToSegment();
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        onClick={addStepToSegment}
                        disabled={!stepPrompt.trim() || addingStep}
                      >
                        {addingStep ? (
                          <RefreshCw className="h-3 w-3 animate-spin" />
                        ) : (
                          <Plus className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    
                    {/* Quick Screenshot Button for Segments */}
                    <Button
                      onClick={() => {
                        const screenshotStep = {
                          id: `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                          order: (editingSegment.steps?.length || 0) + 1,
                          action: "screenshot",
                          description: "Take screenshot",
                          wait_before: 0,
                          wait_after: 500,
                          assertions: [],
                          variables: {},
                          is_optional: false
                        };
                        
                        setEditingSegment(prev => ({
                          ...prev,
                          steps: [...(prev.steps || []), screenshotStep]
                        }));
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full mt-2"
                    >
                      <Camera className="h-3 w-3 mr-2" />
                      Add Screenshot Step
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowSegmentModal(false);
                setEditingSegment(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!editingSegment.name?.trim()) {
                  showWarning('Please enter a segment name');
                  return;
                }
                
                updateTestSegment(editingSegment.id, {
                  name: editingSegment.name,
                  description: editingSegment.description,
                  segment_type: editingSegment.segment_type,
                  steps: editingSegment.steps || []
                });
              }}
              disabled={loading || !editingSegment?.name?.trim()}
            >
              <Target className="h-4 w-4 mr-2" />
              Update Segment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Test Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
            <DialogContent className="max-w-7xl h-[90vh] flex flex-col p-0">
              <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
                <DialogTitle>Edit Flow Test</DialogTitle>
                <DialogDescription>
                  Update your flow test configuration and steps.
                </DialogDescription>
              </DialogHeader>
              
              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <Input
                      placeholder="Flow Test Name"
                      value={testForm.name}
                      onChange={(e) => setTestForm({...testForm, name: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Textarea
                    placeholder="Test description"
                    value={testForm.description}
                    onChange={(e) => setTestForm({...testForm, description: e.target.value})}
                  />
                </div>

                {/* Project Selector in Edit Modal */}
                {projects.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center">
                      <Folder className="h-4 w-4 mr-2 text-purple-600" />
                      Project (Optional)
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={testForm.project_id || ''}
                      onChange={(e) => setTestForm({...testForm, project_id: e.target.value || null})}
                    >
                      <option value="">No Project (Uncategorized)</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.icon} {project.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Organize your tests into projects</p>
                  </div>
                )}

                {/* Enhanced Step Management Section */}
                <div className="border rounded-lg p-4 space-y-4">
                  <h3 className="text-lg font-semibold">Test Steps Management</h3>
                  
                  <div className="grid grid-cols-2 gap-6">
                    {/* Left Side: Steps List */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Current Steps ({testForm.steps?.length || 0})</h4>
                        <div className="flex items-center space-x-2">
                          {selectedStepsForSegment.length > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                              onClick={() => setShowCreateSegmentFromStepsModal(true)}
                            >
                              <Target className="h-3 w-3 mr-1" />
                              Create Segment ({selectedStepsForSegment.length})
                            </Button>
                          )}
                          {testForm.steps?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              Select steps to create segments
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-3 bg-gray-50">
                        {testForm.steps?.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <Code className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            <p className="text-sm">No steps yet</p>
                            <p className="text-xs">Add steps using the prompt area →</p>
                          </div>
                        ) : (
                          testForm.steps.map((step, index) => (
                            <div key={index} className="p-3 bg-white rounded-lg border hover:shadow-sm transition-shadow">
                              <div className="flex items-start space-x-2">
                                {/* Step selection checkbox */}
                                <div className="flex items-start pt-2">
                                  <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={selectedStepsForSegment.includes(index)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedStepsForSegment(prev => [...prev, index]);
                                      } else {
                                        setSelectedStepsForSegment(prev => prev.filter(i => i !== index));
                                      }
                                    }}
                                  />
                                </div>
                                
                                <div className="flex flex-col space-y-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                                    onClick={() => index > 0 && moveStep(index, index - 1)}
                                    disabled={index === 0}
                                    title="Move up"
                                  >
                                    ↑
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                                    onClick={() => index < testForm.steps.length - 1 && moveStep(index, index + 1)}
                                    disabled={index === testForm.steps.length - 1}
                                    title="Move down"
                                  >
                                    ↓
                                  </Button>
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <Badge variant="secondary" className="text-xs font-mono">
                                      {index + 1}
                                    </Badge>
                                    <span className="text-sm font-medium text-gray-900">
                                      {convertStepToHumanReadable(step)}
                                    </span>
                                  </div>
                                  
                                  {/* Show actual URL for navigation steps */}
                                  {step.action === 'navigate' && step.value && (
                                    <div className="bg-blue-50 p-2 rounded text-xs mb-2">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="font-medium text-blue-800">Target URL:</div>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                                          onClick={() => openSelectorEditor(index)}
                                          title="Edit URL and timeout"
                                        >
                                          <Edit className="h-3 w-3 mr-1" />
                                          Edit
                                        </Button>
                                      </div>
                                      <code className="text-xs bg-white px-2 py-1 rounded block break-all">
                                        {step.value}
                                      </code>
                                      {step.value.includes('{{') && (
                                        <div className="text-xs text-blue-600 mt-1">
                                          🔗 Using environment variable
                                        </div>
                                      )}
                                      {!step.value.includes('{{') && step.value.startsWith('http') && (
                                        <div className="text-xs text-blue-600 mt-1">
                                          🌐 Direct URL from prompt
                                        </div>
                                      )}
                                      <div className="text-xs text-gray-500 mt-2">
                                        ⏱️ Timeout: {(step.timeout || 30000) / 1000}s
                                      </div>
                                    </div>
                                  )}
                                  
                                  {step.selector && (
                                    <div className="bg-gray-50 p-2 rounded text-xs space-y-1">
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium text-gray-700">Web Element:</span>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                                          onClick={() => openSelectorEditor(index)}
                                          title="Edit web element selector"
                                        >
                                          <Edit className="h-3 w-3 mr-1" />
                                          Edit
                                        </Button>
                                      </div>
                                      <code className="text-xs bg-white px-2 py-1 rounded block break-all">
                                        {step.selector.type}: {step.selector.value}
                                      </code>
                                      {step.selector.fallback_selectors?.length > 0 && (
                                        <div className="text-xs text-gray-600">
                                          +{step.selector.fallback_selectors.length} fallback(s)
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {step.description && step.description !== convertStepToHumanReadable(step) && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {step.description}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="flex items-center space-x-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                                    onClick={() => copyStep(index)}
                                    title="Copy step"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                                    onClick={() => openDeleteStepModal(index)}
                                    title="Delete step"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Right Side: Add Steps */}
                    <div className="space-y-3">
                      <h4 className="font-medium">Add New Step</h4>
                      
                      {/* Insert Existing Segment */}
                      {testSegments.length > 0 && (
                        <div className="bg-blue-50 p-3 rounded-lg space-y-2">
                          <h5 className="font-medium text-blue-800 text-sm">Insert Existing Segment</h5>
                          <select
                            className="w-full p-2 text-sm border border-blue-200 rounded-md"
                            onChange={(e) => {
                              if (e.target.value) {
                                const segmentId = e.target.value;
                                const position = testForm.steps?.length || 0;
                                insertTestSegment(editingTest.id, segmentId, position);
                                e.target.value = ''; // Reset selection
                              }
                            }}
                          >
                            <option value="">Select a segment to insert...</option>
                            {testSegments.map(segment => (
                              <option key={segment.id} value={segment.id}>
                                {segment.name} ({segment.step_count} steps)
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-blue-600">
                            💡 Segments will be added at the end of current steps
                          </p>
                        </div>
                      )}
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Natural Language Prompt
                          </label>
                          <Textarea
                            placeholder="Describe what you want the test to do...

Examples:
• click submit button
• type hello@example.com in email field  
• select Option 1 from dropdown
• verify success message appears
• scroll to bottom of page"
                            value={stepPrompt}
                            onChange={(e) => setStepPrompt(e.target.value)}
                            rows={6}
                            className="resize-none"
                          />
                        </div>
                        
                        <Button
                          onClick={addStepFromPrompt}
                          disabled={!stepPrompt.trim() || addingStep}
                          className="w-full"
                        >
                          {addingStep ? (
                            <>
                              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                              Converting...
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-2" />
                              Add Step
                            </>
                          )}
                        </Button>
                        
                        {/* Quick Screenshot Action */}
                        <Button
                          onClick={() => {
                            const screenshotStep = {
                              id: `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                              order: (testForm.steps?.length || 0) + 1,
                              action: "screenshot",
                              description: "Take screenshot",
                              wait_before: 0,
                              wait_after: 500,
                              assertions: [],
                              variables: {},
                              is_optional: false
                            };
                            
                            setTestForm(prev => ({
                              ...prev,
                              steps: [...(prev.steps || []), screenshotStep]
                            }));
                          }}
                          variant="outline"
                          size="sm"
                          className="w-full"
                        >
                          <Camera className="h-4 w-4 mr-2" />
                          Add Screenshot Step
                        </Button>
                        
                        <div className="text-xs text-gray-500 p-2 bg-blue-50 rounded border-l-2 border-blue-300">
                          <strong>💡 Tips:</strong>
                          <ul className="mt-1 space-y-1 list-disc list-inside">
                            <li>Be specific: "click red submit button"</li>
                            <li>Include context: "type email in login form"</li>
                            <li>Use action words: click, type, select, verify</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
              
              {/* Sticky Footer with Action Buttons */}
              <div className="flex-shrink-0 border-t bg-gray-50 px-6 py-4">
                <div className="flex justify-between">
                  <div className="flex space-x-2">
                    {/* Show Continue Recording button only for draft tests */}
                    {editingTest && editingTest.status === 'draft' && (
                      <Button
                        variant="outline"
                        className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                        onClick={() => {
                          // Open LiveRecorder with this test to continue
                          setShowEditModal(false);
                          setTestToResume({
                            id: editingTest.id,
                            name: editingTest.name,
                            description: editingTest.description,
                            steps: editingTest.steps || []
                          });
                          setShowLiveRecorder(true);
                        }}
                      >
                        <Video className="h-4 w-4 mr-2" />
                        Continue Recording
                      </Button>
                    )}
                    {/* Show Resume VNC Recording for vnc-recorded tests */}
                    {editingTest && editingTest.tags?.includes('vnc-recorded') && (
                      <Button
                        variant="outline"
                        className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                        onClick={() => {
                          // Find the last URL from the test steps (navigate actions or last recorded URL)
                          const steps = editingTest.steps || [];
                          let lastUrl = '';
                          
                          // Look for navigate steps or extract URL from step data
                          for (let i = steps.length - 1; i >= 0; i--) {
                            const step = steps[i];
                            if (step.action === 'navigate' && step.value) {
                              lastUrl = step.value;
                              break;
                            }
                            if (step.selector?.type === 'url' && step.selector?.value) {
                              lastUrl = step.selector.value;
                              break;
                            }
                          }
                          
                          // If no URL found, try the first navigate step
                          if (!lastUrl) {
                            const firstNavigate = steps.find(s => s.action === 'navigate');
                            lastUrl = firstNavigate?.value || firstNavigate?.selector?.value || '';
                          }
                          
                          setShowEditModal(false);
                          setTestToResume({
                            id: editingTest.id,
                            name: editingTest.name,
                            description: editingTest.description,
                            steps: steps,
                            lastUrl: lastUrl
                          });
                          setShowVNCRecorder(true);
                        }}
                      >
                        <Monitor className="h-4 w-4 mr-2" />
                        Resume VNC Recording
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                      onClick={() => {
                        analyzeVariableUsage();
                        setShowVariablesModal(true);
                      }}
                    >
                      <Database className="h-4 w-4 mr-2" />
                      Manage Variables ({Object.keys(testForm.test_variables || {}).length})
                    </Button>
                    <Button
                      variant="outline"
                      className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                      onClick={async () => {
                        // Save test first, then run it
                        await updateFlowTest();
                        if (editingTest) {
                          // Get the updated test data
                          const updatedTest = flowTests.find(t => t.id === editingTest.id) || editingTest;
                          executeFlowTest(updatedTest.id);
                          setShowEditModal(false);
                        }
                      }}
                      disabled={loading || !testForm.name.trim()}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Save & Run Test
                    </Button>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingTest(null);
                        resetTestForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={updateFlowTest}
                      disabled={loading || !testForm.name.trim()}
                    >
                      {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
                      Update Test
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Test Variables Management Modal */}
          <Dialog open={showVariablesModal} onOpenChange={setShowVariablesModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Test Variables</DialogTitle>
                <DialogDescription>
                  Manage reusable variables for this test. Reference them in test steps using {' '}
                  <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{variableName}}'}</code>
                </DialogDescription>
              </DialogHeader>
              <TestVariablesManager
                testVariables={testForm.test_variables || {}}
                sensitiveVariables={testForm.sensitive_variables || []}
                testSteps={testForm.steps || []}
                onUpdate={(variables, sensitiveVars) => {
                  setTestForm({
                    ...testForm,
                    test_variables: variables,
                    sensitive_variables: sensitiveVars
                  });
                }}
              />
              <div className="flex justify-end space-x-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowVariablesModal(false)}
                >
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Web Element Selector Editor Modal */}
          <Dialog open={showSelectorModal} onOpenChange={setShowSelectorModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>
                  {editingStepIndex !== null && testForm.steps[editingStepIndex]?.action === 'navigate' 
                    ? 'Edit Navigation URL & Timeout' 
                    : 'Edit Web Element Selector & Timeout'}
                </DialogTitle>
                <p className="text-sm text-gray-600">
                  {editingStepIndex !== null && testForm.steps[editingStepIndex]?.action === 'navigate'
                    ? 'Modify the navigation URL and timeout duration for this step'
                    : 'Modify the selector to target the correct element and adjust timeout'}
                </p>
              </DialogHeader>
              
              <div className="space-y-6 overflow-y-auto pr-2" style={{maxHeight: 'calc(90vh - 180px)'}}>
                {/* Step Title */}
                <div className="space-y-3">
                  <h3 className="text-lg font-medium">Step Title</h3>
                  <div>
                    <label className="block text-sm font-medium mb-2">Step Description</label>
                    <Input
                      placeholder="Enter a descriptive title for this step..."
                      value={selectorForm.stepTitle}
                      onChange={(e) => setSelectorForm({...selectorForm, stepTitle: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This title helps identify the step's purpose. Remove "(Copy)" suffix and make it more descriptive.
                    </p>
                  </div>
                </div>

                {/* Navigation URL Editor (only for navigate steps) */}
                {editingStepIndex !== null && testForm.steps[editingStepIndex]?.action === 'navigate' && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium">🌐 Navigation URL</h3>
                    <div>
                      <label className="block text-sm font-medium mb-2">Target URL</label>
                      <Input
                        placeholder="https://example.com"
                        value={testForm.steps[editingStepIndex]?.value || ''}
                        onChange={(e) => {
                          const newSteps = [...testForm.steps];
                          newSteps[editingStepIndex] = {
                            ...newSteps[editingStepIndex],
                            value: e.target.value
                          };
                          setTestForm({...testForm, steps: newSteps});
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the full URL to navigate to. You can use environment variables like {'{{base_url}}'}.
                      </p>
                    </div>
                  </div>
                )}

                {/* Value/Data Field (for type, select, and other data-driven actions) */}
                {editingStepIndex !== null && 
                 testForm.steps[editingStepIndex]?.action !== 'navigate' && 
                 ['type', 'select', 'fill', 'press', 'selectOption'].includes(testForm.steps[editingStepIndex]?.action) && (
                  <div className="space-y-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <Database className="h-5 w-5 text-purple-600" />
                      <h3 className="text-lg font-medium text-purple-900">Input Data / Value</h3>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-purple-800">
                        Data to Enter
                        {Object.keys(testForm.test_variables || {}).length > 0 && (
                          <span className="ml-2 text-xs font-normal text-purple-600">
                            ({Object.keys(testForm.test_variables || {}).length} variable{Object.keys(testForm.test_variables || {}).length !== 1 ? 's' : ''} available)
                          </span>
                        )}
                      </label>
                      <Input
                        placeholder="Enter static text or use {{variableName}}"
                        value={testForm.steps[editingStepIndex]?.value || ''}
                        onChange={(e) => {
                          const newSteps = [...testForm.steps];
                          newSteps[editingStepIndex] = {
                            ...newSteps[editingStepIndex],
                            value: e.target.value
                          };
                          setTestForm({...testForm, steps: newSteps});
                        }}
                      />
                      <div className="mt-2 space-y-2">
                        <p className="text-xs text-purple-700">
                          💡 <strong>Use variables:</strong> Type <code className="bg-purple-100 px-1 rounded">&#123;&#123;variableName&#125;&#125;</code> to reference test variables
                        </p>
                        
                        {Object.keys(testForm.test_variables || {}).length > 0 && (
                          <div className="bg-white rounded-lg p-3 border border-purple-200">
                            <p className="text-xs font-medium text-purple-900 mb-2">Available Variables:</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.keys(testForm.test_variables || {}).map(varName => (
                                <button
                                  key={varName}
                                  onClick={() => {
                                    const newSteps = [...testForm.steps];
                                    const currentValue = newSteps[editingStepIndex]?.value || '';
                                    newSteps[editingStepIndex] = {
                                      ...newSteps[editingStepIndex],
                                      value: currentValue + `{{${varName}}}`
                                    };
                                    setTestForm({...testForm, steps: newSteps});
                                  }}
                                  className="inline-flex items-center px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded hover:bg-purple-200 transition-colors"
                                  title={`Click to insert {{${varName}}}`}
                                >
                                  <Database className="h-3 w-3 mr-1" />
                                  {varName}
                                  {testForm.sensitive_variables?.includes(varName) && (
                                    <span className="ml-1">🔒</span>
                                  )}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={() => {
                                analyzeVariableUsage();
                                setShowVariablesModal(true);
                              }}
                              className="mt-2 text-xs text-purple-600 hover:text-purple-700 underline"
                            >
                              Manage Variables
                            </button>
                          </div>
                        )}
                        
                        {Object.keys(testForm.test_variables || {}).length === 0 && (
                          <div className="bg-white rounded-lg p-3 border border-purple-200">
                            <p className="text-xs text-purple-700">
                              No variables defined yet.{' '}
                              <button
                                onClick={() => {
                                  analyzeVariableUsage();
                                  setShowVariablesModal(true);
                                }}
                                className="text-purple-600 hover:text-purple-700 underline font-medium"
                              >
                                Create your first variable
                              </button>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Primary Selector (only for non-navigate steps or steps with selectors) */}
                {(editingStepIndex === null || testForm.steps[editingStepIndex]?.action !== 'navigate') && (
                  <>
                    <div className="space-y-3">
                      <h3 className="text-lg font-medium">Primary Selector</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Selector Type</label>
                          <select
                            className="w-full p-2 border border-gray-300 rounded-md"
                            value={selectorForm.type}
                            onChange={(e) => setSelectorForm({...selectorForm, type: e.target.value})}
                          >
                            <option value="css">CSS Selector</option>
                            <option value="xpath">XPath</option>
                            <option value="text">Text Content</option>
                            <option value="placeholder">Placeholder Text</option>
                            <option value="label">Label Text</option>
                            <option value="role">ARIA Role</option>
                          </select>
                        </div>
                        
                        <div className="col-span-2">
                          <label className="block text-sm font-medium mb-2">Selector Value</label>
                          <Input
                            placeholder={`Enter ${selectorForm.type} selector...`}
                            value={selectorForm.value}
                            onChange={(e) => setSelectorForm({...selectorForm, value: e.target.value})}
                          />
                        </div>
                      </div>
                  
                  {/* Selector Examples */}
                  <div className="bg-blue-50 p-3 rounded-lg text-sm">
                    <p className="font-medium text-blue-800 mb-2">💡 Examples for {selectorForm.type}:</p>
                    <div className="text-blue-700 space-y-1">
                      {selectorForm.type === 'css' && (
                        <>
                          <div><code>button[type="submit"]</code> - Button by type</div>
                          <div><code>#login-button</code> - Element by ID</div>
                          <div><code>.btn-primary</code> - Element by class</div>
                          <div><code>[data-testid="submit"]</code> - Element by test ID</div>
                        </>
                      )}
                      {selectorForm.type === 'xpath' && (
                        <>
                          <div><code>//button[text()="Submit"]</code> - Button by text</div>
                          <div><code>//input[@name="email"]</code> - Input by name</div>
                          <div><code>//div[@class="error"]</code> - Div by class</div>
                        </>
                      )}
                      {selectorForm.type === 'text' && (
                        <>
                          <div><code>Submit</code> - Exact text match</div>
                          <div><code>Click here</code> - Link or button text</div>
                        </>
                      )}
                      {selectorForm.type === 'placeholder' && (
                        <>
                          <div><code>Enter your email</code> - Input placeholder</div>
                          <div><code>Search...</code> - Search field placeholder</div>
                        </>
                      )}
                      {selectorForm.type === 'label' && (
                        <>
                          <div><code>Email Address</code> - Form label text</div>
                          <div><code>Password</code> - Password field label</div>
                        </>
                      )}
                      {selectorForm.type === 'role' && (
                        <>
                          <div><code>button</code> - Any button element</div>
                          <div><code>textbox</code> - Input field</div>
                          <div><code>link</code> - Link element</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fallback Selectors (only for non-navigate steps) */}
                {(editingStepIndex === null || testForm.steps[editingStepIndex]?.action !== 'navigate') && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">Fallback Selectors</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addFallbackSelector}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Fallback
                    </Button>
                  </div>
                  
                  <p className="text-sm text-gray-600">
                    Add backup selectors in case the primary selector fails
                  </p>
                  
                  {selectorForm.fallback_selectors.map((fallback, index) => (
                    <div key={index} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                      <Badge className="text-xs">#{index + 1}</Badge>
                      
                      <select
                        className="p-2 border border-gray-300 rounded-md"
                        value={fallback.type}
                        onChange={(e) => updateFallbackSelector(index, 'type', e.target.value)}
                      >
                        <option value="css">CSS</option>
                        <option value="xpath">XPath</option>
                        <option value="text">Text</option>
                        <option value="placeholder">Placeholder</option>
                        <option value="label">Label</option>
                        <option value="role">Role</option>
                      </select>
                      
                      <Input
                        placeholder={`Fallback ${fallback.type} selector...`}
                        value={fallback.value}
                        onChange={(e) => updateFallbackSelector(index, 'value', e.target.value)}
                        className="flex-1"
                      />
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFallbackSelector(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {selectorForm.fallback_selectors.length === 0 && (
                    <div className="text-center py-6 text-gray-500">
                      <Target className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">No fallback selectors added</p>
                      <p className="text-xs">Add backup selectors for more reliable tests</p>
                    </div>
                  )}
                </div>
                )}
                </>
                )}

                {/* Step Timeout Settings - Always visible for all steps */}
                <div className="space-y-3">
                  <h3 className="text-lg font-medium">⚙️ Step Timeout Settings</h3>
                  <p className="text-sm text-gray-600">
                    The maximum number of seconds that this step will be retried before timing out
                  </p>
                  
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Timeout Duration</span>
                      <span className="text-lg font-bold text-blue-600">{(selectorForm.timeout / 1000).toFixed(0)}s</span>
                    </div>
                    
                    <input
                      type="range"
                      min="4"
                      max="120"
                      step="5"
                      value={selectorForm.timeout / 1000}
                      onChange={(e) => setSelectorForm({...selectorForm, timeout: parseInt(e.target.value) * 1000})}
                      className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((selectorForm.timeout / 1000 - 4) / (120 - 4)) * 100}%, #d1d5db ${((selectorForm.timeout / 1000 - 4) / (120 - 4)) * 100}%, #d1d5db 100%)`
                      }}
                    />
                    
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>4s (Fast)</span>
                      <span>15s (Default)</span>
                      <span>120s (Slow)</span>
                    </div>
                    
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-blue-800">
                        💡 <strong>Tip:</strong> Use shorter timeouts (4-10s) for elements that should appear quickly. 
                        Use longer timeouts (30-120s) for slow loading content or network-dependent actions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowSelectorModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={updateStepSelector}
                  disabled={
                    editingStepIndex !== null && testForm.steps[editingStepIndex]?.action === 'navigate'
                      ? false // Navigation steps: always allow save (URL is edited separately)
                      : !selectorForm.value.trim() // Other steps: require selector value
                  }
                >
                  <Target className="h-4 w-4 mr-2" />
                  Update Selector
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Environment Modal */}
          <Dialog open={showEditEnvironmentModal} onOpenChange={setShowEditEnvironmentModal}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Environment</DialogTitle>
                <p className="text-sm text-gray-500">Update environment settings and credential profiles</p>
              </DialogHeader>
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <Input
                      placeholder="e.g., Production, Staging, Development"
                      value={environmentForm.name}
                      onChange={(e) => setEnvironmentForm({...environmentForm, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <Textarea
                      placeholder="Brief description of this environment"
                      value={environmentForm.description}
                      onChange={(e) => setEnvironmentForm({...environmentForm, description: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Base URL *</label>
                    <Input
                      placeholder="https://app.example.com"
                      value={environmentForm.base_url}
                      onChange={(e) => setEnvironmentForm({...environmentForm, base_url: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Variables (JSON)</label>
                    <Textarea
                      placeholder='{"api_key": "xxx", "timeout": "30"}'
                      value={environmentVariablesText}
                      onChange={(e) => setEnvironmentVariablesText(e.target.value)}
                      className="font-mono text-sm"
                      rows={3}
                    />
                  </div>
                </div>
                
                {/* Credential Profiles Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="block text-sm font-medium">Credential Profiles</label>
                      <p className="text-xs text-gray-500">Manage session cookies for authenticated testing</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddProfileForm(true)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Profile
                    </Button>
                  </div>
                  
                  {/* List of credential profiles */}
                  {credentialProfiles.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {credentialProfiles.map((profile, idx) => (
                        <div key={profile.id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{profile.name}</p>
                              <p className="text-xs text-gray-500">
                                {profile.cookies?.length || 0} cookies
                                {profile.is_default && <span className="ml-2 text-green-600 font-medium">(Default)</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {!profile.is_default && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs"
                                onClick={() => {
                                  const updatedProfiles = credentialProfiles.map((p, i) => ({
                                    ...p,
                                    is_default: i === idx
                                  }));
                                  setCredentialProfiles(updatedProfiles);
                                }}
                              >
                                Set Default
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-600 hover:text-gray-700 hover:bg-gray-100"
                              onClick={() => {
                                // Populate form with profile data for editing
                                setNewProfileForm({
                                  name: profile.name,
                                  description: profile.description || '',
                                  cookies: JSON.stringify(profile.cookies || [], null, 2),
                                  headers: JSON.stringify(profile.headers || {}, null, 2),
                                  is_default: profile.is_default || false
                                });
                                setEditingProfileIndex(idx);
                                setShowAddProfileForm(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setCredentialProfiles(credentialProfiles.filter((_, i) => i !== idx));
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add/Edit Profile Form */}
                  {showAddProfileForm && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">
                          {editingProfileIndex !== null ? 'Edit Credential Profile' : 'New Credential Profile'}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowAddProfileForm(false);
                            setEditingProfileIndex(null);
                            setNewProfileForm({ name: '', description: '', cookies: '', headers: '', is_default: false });
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Profile Name *</label>
                        <Input
                          placeholder="e.g., Admin User, Regular User"
                          value={newProfileForm.name}
                          onChange={(e) => setNewProfileForm({...newProfileForm, name: e.target.value})}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Session Cookies (JSON Array) *</label>
                        <Textarea
                          placeholder='[{"name": "session_id", "value": "abc123", "domain": ".example.com"}]'
                          value={newProfileForm.cookies}
                          onChange={(e) => setNewProfileForm({...newProfileForm, cookies: e.target.value})}
                          className="font-mono text-xs"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Custom Headers (JSON, optional)</label>
                        <Input
                          placeholder='{"Authorization": "Bearer xxx"}'
                          value={newProfileForm.headers}
                          onChange={(e) => setNewProfileForm({...newProfileForm, headers: e.target.value})}
                          className="font-mono text-xs"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="edit_is_default_profile"
                          checked={newProfileForm.is_default}
                          onChange={(e) => setNewProfileForm({...newProfileForm, is_default: e.target.checked})}
                          className="rounded"
                        />
                        <label htmlFor="edit_is_default_profile" className="text-xs">Set as default profile</label>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          if (!newProfileForm.name.trim()) {
                            showWarning('Profile name is required');
                            return;
                          }
                          try {
                            const cookies = newProfileForm.cookies.trim() ? JSON.parse(newProfileForm.cookies) : [];
                            const headers = newProfileForm.headers.trim() ? JSON.parse(newProfileForm.headers) : {};
                            
                            // If this is set as default, remove default from others
                            let updatedProfiles = [...credentialProfiles];
                            if (newProfileForm.is_default) {
                              updatedProfiles = updatedProfiles.map(p => ({...p, is_default: false}));
                            }
                            
                            if (editingProfileIndex !== null) {
                              // Update existing profile
                              updatedProfiles[editingProfileIndex] = {
                                ...updatedProfiles[editingProfileIndex],
                                name: newProfileForm.name.trim(),
                                description: newProfileForm.description.trim(),
                                cookies,
                                headers,
                                is_default: newProfileForm.is_default
                              };
                              showSuccess('Profile updated successfully');
                            } else {
                              // Add new profile
                              updatedProfiles.push({
                                id: `profile_${Date.now()}`,
                                name: newProfileForm.name.trim(),
                                description: newProfileForm.description.trim(),
                                cookies,
                                headers,
                                is_default: newProfileForm.is_default
                              });
                            }
                            
                            setCredentialProfiles(updatedProfiles);
                            setShowAddProfileForm(false);
                            setEditingProfileIndex(null);
                            setNewProfileForm({ name: '', description: '', cookies: '', headers: '', is_default: false });
                          } catch (err) {
                            showWarning('Invalid JSON format in cookies or headers');
                          }
                        }}
                        disabled={!newProfileForm.name.trim()}
                      >
                        {editingProfileIndex !== null ? 'Update Profile' : 'Add Profile'}
                      </Button>
                    </div>
                  )}
                  
                  {credentialProfiles.length === 0 && !showAddProfileForm && (
                    <div className="text-center py-4 text-gray-500 text-sm border rounded-lg bg-gray-50">
                      No credential profiles. Click "Add Profile" to add session cookies for authenticated testing.
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowEditEnvironmentModal(false);
                      setEditingEnvironment(null);
                      resetEnvironmentForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={updateEnvironment}
                    disabled={loading || !environmentForm.name.trim() || !environmentForm.base_url.trim()}
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
                    Update Environment
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

      {/* Execution Details Modal */}
      <Dialog open={showExecutionDetails} onOpenChange={setShowExecutionDetails}>
        <DialogContent className="max-w-[95vw] md:max-w-6xl max-h-[95vh] overflow-hidden p-0">
          <div className="overflow-y-auto max-h-[calc(95vh-2rem)] overflow-x-hidden">
            <div className="p-6">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl font-bold">
                  Execution Details
                </DialogTitle>
              </DialogHeader>
              {selectedExecution && (
                <div className="space-y-4 mt-4 w-full overflow-x-hidden">
                  {/* Execution Info Card - Always Visible */}
                  <Card className="border-2 border-blue-200 bg-blue-50/50 w-full overflow-hidden">
                    <div className="p-4 w-full overflow-x-hidden">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                        {/* Left Column - Execution Info */}
                        <div className="space-y-2 min-w-0 overflow-hidden">{/* min-w-0 for proper text truncation */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600">Execution Number:</span>
                        <Badge variant="outline" className="font-mono">
                          #{selectedExecution?.execution_number || 'N/A'}
                        </Badge>
                      </div>
                      <div className="flex items-start gap-2 w-full overflow-hidden">
                        <span className="text-sm font-medium text-gray-600 whitespace-nowrap flex-shrink-0">Execution ID:</span>
                        <code className="text-xs bg-white px-2 py-1 rounded border font-mono break-all flex-1 min-w-0 overflow-hidden">
                          {selectedExecution?.id || 'Unknown'}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 flex-shrink-0 hover:bg-blue-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(selectedExecution?.id || '');
                            // Show toast notification
                            const toast = document.createElement('div');
                            toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2';
                            toast.innerHTML = '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg><span>Execution ID copied to clipboard!</span>';
                            document.body.appendChild(toast);
                            setTimeout(() => {
                              toast.style.opacity = '0';
                              toast.style.transition = 'opacity 0.3s';
                              setTimeout(() => toast.remove(), 300);
                            }, 2000);
                          }}
                          title="Copy execution ID to clipboard"
                        >
                          <Copy className="h-3.5 w-3.5 text-gray-600" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600">Status:</span>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(selectedExecution?.status)}
                          <Badge className={getStatusColor(selectedExecution?.status)}>
                            {selectedExecution?.status || 'Unknown'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Column - Timing Info */}
                    <div className="space-y-2 min-w-0 overflow-hidden">
                      <div className="text-sm w-full overflow-hidden">
                        <span className="font-medium text-gray-600">Started:</span>
                        <div className="text-gray-800 break-words overflow-hidden">
                          {selectedExecution?.started_at ? new Date(selectedExecution.started_at).toLocaleString() : 'Unknown'}
                        </div>
                      </div>
                      {selectedExecution.finished_at && (
                        <div className="text-sm w-full overflow-hidden">
                          <span className="font-medium text-gray-600">Finished:</span>
                          <div className="text-gray-800 break-words overflow-hidden">
                            {new Date(selectedExecution.finished_at).toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedExecution.duration_ms && (
                        <div className="text-sm w-full overflow-hidden">
                          <span className="font-medium text-gray-600">Duration:</span>
                          <span className="text-gray-800 ml-2 font-mono">
                            {(selectedExecution.duration_ms / 1000).toFixed(2)}s
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Configuration Row */}
                  <div className="mt-3 pt-3 border-t border-blue-200 w-full overflow-x-hidden">
                    <div className="flex flex-wrap items-center gap-4 text-sm overflow-hidden">
                      <div className="flex items-center gap-2">
                        {getBrowserIcon(selectedExecution?.browser)}
                        <span className="text-gray-700">{selectedExecution?.browser || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {getDeviceIcon(selectedExecution?.device_type)}
                        <span className="text-gray-700">{selectedExecution?.device_type || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedExecution?.is_headless ? (
                          <EyeOff className="h-4 w-4 text-gray-600" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-600" />
                        )}
                        <span className="text-gray-700">{selectedExecution?.is_headless ? 'Headless' : 'Headed'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Error Message - Collapsible */}
              {selectedExecution.error_message && (
                <Card className="border-2 border-red-300 bg-red-50 w-full overflow-hidden">
                  <div className="p-4 w-full overflow-x-hidden">
                    <div 
                      className="flex items-center justify-between gap-2 w-full overflow-hidden"
                    >
                      <div 
                        className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden cursor-pointer"
                        onClick={() => setShowErrorDetails(!showErrorDetails)}
                      >
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="font-semibold text-red-800">Execution Error</div>
                          {!showErrorDetails && (
                            <div className="text-sm text-red-700 truncate max-w-full overflow-hidden">
                              {selectedExecution.error_message.substring(0, 80)}
                              {selectedExecution.error_message.length > 80 && '...'}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800 hover:bg-red-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(selectedExecution.error_message);
                            // Show toast notification
                            const toast = document.createElement('div');
                            toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2';
                            toast.innerHTML = '<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg><span>Error copied to clipboard!</span>';
                            document.body.appendChild(toast);
                            setTimeout(() => {
                              toast.style.opacity = '0';
                              toast.style.transition = 'opacity 0.3s';
                              setTimeout(() => toast.remove(), 300);
                            }, 2000);
                          }}
                          title="Copy error to clipboard"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800 hover:bg-red-100"
                          onClick={() => setShowErrorDetails(!showErrorDetails)}
                        >
                          {showErrorDetails ? (
                            <>
                              <ChevronUp className="h-4 w-4" />
                              <span className="ml-1 hidden sm:inline">Hide</span>
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4" />
                              <span className="ml-1 hidden sm:inline">Show</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {showErrorDetails && (
                      <div className="mt-3 pt-3 border-t border-red-200 w-full overflow-x-hidden">
                        <pre className="text-xs text-red-800 whitespace-pre-wrap break-all bg-white p-3 rounded border border-red-200 max-h-64 overflow-y-auto overflow-x-hidden font-mono w-full max-w-full">
                          {selectedExecution.error_message}
                        </pre>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Execution Results */}
              {selectedExecution.results && selectedExecution.results.length > 0 && (
                <div className="w-full overflow-x-hidden">
                  <h4 className="font-semibold mb-3">Step Results ({selectedExecution.results.length})</h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto overflow-x-hidden">
                    {selectedExecution.results.map((result, index) => (
                      <Card key={index} className="p-3 sm:p-4">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {getStatusIcon(result.status)}
                              <span className="font-medium break-words">{result.message}</span>
                              <Badge className={getStatusColor(result.status)} size="sm">
                                {result.status}
                              </Badge>
                            </div>
                            {result.execution_time_ms && (
                              <div className="text-sm text-gray-600 mb-2">
                                ⏱️ Execution time: {result.execution_time_ms}ms
                              </div>
                            )}
                            {result.error_details && (
                              <div className="text-sm text-red-600 bg-red-50 p-2 rounded mb-2 overflow-x-auto">
                                <strong>Error Details:</strong> 
                                <pre className="whitespace-pre-wrap break-words mt-1 text-xs font-mono">
                                  {JSON.stringify(result.error_details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                          {result.screenshot_path && (
                            <div className="flex-shrink-0">
                              <Screenshot 
                                screenshotPath={result.screenshot_path}
                                alt="Step screenshot"
                                className="w-24 h-16 sm:w-32 sm:h-20 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => openScreenshotInNewTab(result.screenshot_path)}
                              />
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Screenshots Gallery */}
              {selectedExecution.screenshots && selectedExecution.screenshots.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Screenshots ({selectedExecution.screenshots.length})</h4>
                  
                  {/* For headed mode with many screenshots, suggest using replay instead */}
                  {!selectedExecution.is_headless && selectedExecution.screenshots.length > 10 ? (
                    <Alert className="border-blue-200 bg-blue-50">
                      <Video className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                          <div>
                            <div className="font-semibold mb-1">Headed Execution Recording Available</div>
                            <div className="text-sm">
                              This execution has {selectedExecution.screenshots.length} screenshots captured during headed mode. 
                              Use the Replay feature for the best viewing experience.
                            </div>
                          </div>
                          <Button
                            onClick={() => {
                              setShowExecutionDetails(false);
                              setReplayExecution(selectedExecution);
                              setShowReplayView(true);
                            }}
                            className="flex items-center gap-2 whitespace-nowrap"
                          >
                            <Video className="h-4 w-4" />
                            Open Replay
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    /* Show screenshot grid for headless or executions with few screenshots */
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                      {selectedExecution.screenshots.slice(0, 12).map((screenshot, index) => (
                        <div key={index} className="border rounded-lg p-2">
                          <Screenshot 
                            screenshotPath={screenshot}
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-24 sm:h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => openScreenshotInNewTab(screenshot)}
                          />
                          <p className="text-xs text-gray-500 mt-1 text-center">
                            Screenshot {index + 1}
                          </p>
                        </div>
                      ))}
                      {selectedExecution.screenshots.length > 12 && (
                        <div className="border rounded-lg p-2 flex items-center justify-center bg-gray-50">
                          <div className="text-center text-sm text-gray-600">
                            <ImageIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            +{selectedExecution.screenshots.length - 12} more
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Console Logs */}
              {selectedExecution.console_logs && selectedExecution.console_logs.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Console Logs ({selectedExecution.console_logs.length})</h4>
                  <div className="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg max-h-64 overflow-y-auto font-mono text-xs sm:text-sm overflow-x-auto">
                    {selectedExecution.console_logs.map((log, index) => (
                      <div key={index} className="mb-1 break-words">
                        <span className="text-gray-500">[{log.timestamp}] [{log.type}]</span>{' '}
                        <span className="break-all">{log.text}</span>
                        {log.location && (
                          <span className="text-blue-400 ml-2 break-all block sm:inline">
                            @ {
                              typeof log.location === 'string' 
                                ? log.location 
                                : log.location.url || JSON.stringify(log.location)
                            }
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Network Logs */}
              {selectedExecution.network_logs && selectedExecution.network_logs.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Network Logs ({selectedExecution.network_logs.length})</h4>
                  <div className="bg-gray-100 p-3 sm:p-4 rounded-lg max-h-64 overflow-y-auto">
                    {selectedExecution.network_logs.map((log, index) => (
                      <div key={index} className="mb-2 pb-2 border-b border-gray-200 last:border-b-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className={log.status >= 400 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                            {log.status || 'ERROR'}
                          </Badge>
                          <span className="font-mono text-sm">{log.method || 'GET'}</span>
                          <span className="text-blue-600 hover:underline cursor-pointer text-sm break-all max-w-full overflow-hidden" 
                                onClick={() => window.open(log.url, '_blank')}
                                title={log.url}>
                            {log.url}
                          </span>
                        </div>
                        {log.error && (
                          <div className="text-red-600 text-sm mt-1 ml-4 overflow-x-auto">
                            <pre className="whitespace-pre-wrap break-words">
                              {log.error}
                            </pre>
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          {log.timestamp}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t mt-4">
                {/* Create Jira Ticket Button - Show only for failed tests */}
                {(selectedExecution?.status === 'failed' || selectedExecution?.error_message) && (
                  <Button
                    onClick={() => createJiraTicketFromExecution(selectedExecution)}
                    disabled={creatingJiraTicket || jiraIntegrations.length === 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                  >
                    {creatingJiraTicket ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Creating Ticket...
                      </>
                    ) : (
                      <>
                        <Bug className="h-4 w-4 mr-2" />
                        Create Jira Ticket
                      </>
                    )}
                  </Button>
                )}
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button 
                    onClick={() => setShowExecutionDetails(false)}
                    variant="outline"
                    className="flex-1 sm:flex-initial"
                  >
                    Close Details
                  </Button>
                </div>
              </div>
            </div>
          )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="tests" className="flex items-center space-x-2">
            <Code className="h-4 w-4" />
            <span>Tests</span>
          </TabsTrigger>
          <TabsTrigger value="segments" className="flex items-center space-x-2">
            <Target className="h-4 w-4" />
            <span>Test Segments</span>
          </TabsTrigger>
          <TabsTrigger value="executions" className="flex items-center space-x-2">
            <Play className="h-4 w-4" />
            <span>Executions</span>
          </TabsTrigger>
          <TabsTrigger value="schedules" className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>Schedules</span>
          </TabsTrigger>
          <TabsTrigger value="environments" className="flex items-center space-x-2">
            <Globe className="h-4 w-4" />
            <span>Environments</span>
          </TabsTrigger>
        </TabsList>

        {/* Tests Tab */}
        <TabsContent value="tests" className="space-y-4">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex justify-between items-center">
              <div>
                <IDTooltip id="FLT-02">
                  <h3 className="text-lg font-semibold">Flow Tests ({flowTests.length})</h3>
                </IDTooltip>
                <p className="text-gray-600 text-sm">Manage and execute your cross-browser UI tests</p>
              </div>
              <Button
                variant="outline"
                onClick={() => loadFlowTests(false)}
                disabled={loading}
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            {/* Search Bar */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tests by name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearch("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Project Filter - Phase 2.2: Only shows if projects exist */}
          {projects.length > 0 && (
            <div className="mb-4 flex items-center space-x-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
              <Folder className="h-5 w-5 text-purple-600 flex-shrink-0" />
              <div className="flex items-center space-x-2 flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Project:
                </label>
                <select
                  value={selectedProjectFilter || ''}
                  onChange={(e) => {
                    const projectId = e.target.value || null;
                    setSelectedProjectFilter(projectId);
                    setTestsOffset(0); // Reset pagination
                    loadFlowTests(false, projectId); // Reload with filter
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                >
                  <option value="">All Projects</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.icon} {project.name}
                    </option>
                  ))}
                </select>
                {selectedProjectFilter && (
                  <button
                    onClick={() => {
                      setSelectedProjectFilter(null);
                      setTestsOffset(0);
                      loadFlowTests(false, null);
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {flowTests.length === 0 ? (
            <Card className="p-8 text-center">
              <Code className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">No Flow Tests yet</p>
              <p className="text-sm text-gray-500">Create your first Flow Test to get started with modern UI testing</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {flowTests.map((test) => (
                <Card key={test.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold">{test.name}</h3>
                        <Badge className={getStatusColor(test.status)}>
                          {test.status}
                        </Badge>
                        {/* Running Execution Indicator */}
                        {runningExecutionsByTest[test.id] && (
                          <Badge className="bg-blue-500 text-white animate-pulse">
                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            {runningExecutionsByTest[test.id].count} Running
                          </Badge>
                        )}
                        {test.tags.map((tag, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      {/* Phase 3: Display Project Name */}
                      {test.project_id && (() => {
                        const project = projects.find(p => p.id === test.project_id);
                        return project ? (
                          <div className="flex items-center space-x-1 mb-2 text-sm text-purple-600">
                            <Folder className="h-3.5 w-3.5" />
                            <span>{project.icon} {project.name}</span>
                          </div>
                        ) : null;
                      })()}
                      <p className="text-gray-600 mb-3">{test.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>📊 {test.execution_count || 0} executions</span>
                        <span>✅ {test.success_rate !== null && test.success_rate !== undefined ? (test.success_rate * 100).toFixed(1) : '0.0'}% success rate</span>
                        {test.last_executed_at && (
                          <span>🕒 Last run: {formatLastRunDateTime(test.last_executed_at)}</span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {/* Primary Actions Row */}
                      <div className="flex items-center space-x-2">
                        {/* Quick Run with Dropdown */}
                        <div className="flex items-center">
                          <Button
                            size="sm"
                            onClick={() => quickRunTest(test, 'headless')}
                            className="bg-purple-600 hover:bg-purple-700 text-white rounded-r-none border-r border-purple-500"
                            disabled={loading}
                          >
                            <Zap className="h-4 w-4 mr-1" />
                            Quick Run
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                className="bg-purple-600 hover:bg-purple-700 text-white rounded-l-none px-2"
                                disabled={loading}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem 
                                onClick={() => quickRunTest(test, 'headless')}
                                className="cursor-pointer"
                              >
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2">
                                    <Zap className="h-4 w-4 text-green-600" />
                                    <div>
                                      <p className="font-medium">Headless Mode</p>
                                      <p className="text-xs text-gray-500">Faster, runs in background</p>
                                    </div>
                                  </div>
                                </div>
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => quickRunTest(test, 'headed')}
                                className="cursor-pointer"
                              >
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2">
                                    <Eye className="h-4 w-4 text-blue-600" />
                                    <div>
                                      <p className="font-medium">Headed Mode</p>
                                      <p className="text-xs text-gray-500">Visual test, see browser</p>
                                    </div>
                                  </div>
                                </div>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => executeFlowTest(test.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          disabled={loading}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Configure & Run
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-purple-50 hover:bg-purple-100 text-purple-600"
                          onClick={() => {
                            setSelectedTestForSchedule(test);
                            setShowScheduleModal(true);
                          }}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Schedule
                        </Button>
                      </div>
                      {/* Secondary Actions Row */}
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedTest(test);
                            loadExecutions(test.id);
                            setActiveTab('executions'); // Automatically switch to executions tab
                          }}
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          History
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditTest(test)}
                          disabled={loading}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        {/* Show Continue Recording button for live-recorded tests */}
                        {test.tags && test.tags.includes('live-recording') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async (e) => {
                              e.stopPropagation(); // Prevent any event bubbling
                              console.log('[FlowTesting] Continue Recording clicked for test:', {
                                id: test.id,
                                name: test.name,
                                has_steps: !!test.steps
                              });
                              
                              try {
                                // Fetch the full test data with steps
                                console.log('[FlowTesting] Fetching full test data...');
                                const response = await axios.get(
                                  `${BACKEND_URL}/api/flow-testing/tests/${test.id}`,
                                  { headers: getAuthHeaders() }
                                );
                                
                                const fullTest = response.data;
                                
                                // Normalize steps to ensure selector is always a string for frontend use
                                if (fullTest.steps) {
                                  fullTest.steps = fullTest.steps.map(step => ({
                                    ...step,
                                    selector: typeof step.selector === 'object' && step.selector?.value 
                                      ? step.selector.value 
                                      : step.selector
                                  }));
                                }
                                
                                console.log('[FlowTesting] Full test fetched:', {
                                  id: fullTest.id,
                                  name: fullTest.name,
                                  steps_count: fullTest.steps?.length || 0,
                                  first_step: fullTest.steps?.[0]?.value || fullTest.steps?.[0]?.url || 'N/A'
                                });
                                
                                setTestToResume(fullTest);
                                setShowLiveRecorder(true);
                              } catch (error) {
                                console.error('[FlowTesting] Failed to fetch full test:', error);
                                showError('Failed to load test data. Please try again.');
                              }
                            }}
                            disabled={loading}
                            className="text-purple-600 hover:text-purple-700 border-purple-600"
                          >
                            <Video className="h-4 w-4 mr-1" />
                            Continue Recording
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteModal(test.id)}
                          className="text-red-600 hover:text-red-700"
                          disabled={loading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              
              {/* Load More Button / Loading Indicator */}
              {loadingMoreTests && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              )}
              
              {hasMoreTests && !loadingMoreTests && flowTests.length > 0 && (
                <div className="flex justify-center py-4">
                  <Button 
                    onClick={() => loadFlowTests(true)}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    Load More Tests
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Test Segments Tab */}
        <TabsContent value="segments" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <IDTooltip id="FLT-03">
                <h2 className="text-2xl font-bold">Test Segments</h2>
              </IDTooltip>
              <p className="text-gray-600">Create reusable test components for multiple Flow Tests</p>
            </div>
            
            <Button 
              onClick={() => setShowCreateSegmentModal(true)}
              disabled={loading}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Segment
            </Button>
          </div>

          {testSegments.length === 0 ? (
            <Card className="p-8 text-center">
              <Target className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold mb-2">No Test Segments Yet</h3>
              <p className="text-gray-600 mb-4">
                Create reusable test segments to share common flows between tests
              </p>
              <div className="space-y-2 text-sm text-gray-500 mb-6">
                <p>• Login flows</p>
                <p>• Navigation patterns</p>
                <p>• Form filling sequences</p>
                <p>• Verification steps</p>
              </div>
              <Button 
                onClick={() => setShowCreateSegmentModal(true)}
                disabled={loading}
              >
                <Target className="h-4 w-4 mr-2" />
                Create Your First Segment
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {testSegments.map((segment) => (
                <Card key={segment.id} className="p-4 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold truncate" title={segment.name}>
                          {segment.name}
                        </h3>
                        <Badge 
                          variant={
                            segment.segment_type === 'login' ? 'default' :
                            segment.segment_type === 'navigation' ? 'secondary' :
                            segment.segment_type === 'form_filling' ? 'outline' :
                            segment.segment_type === 'verification' ? 'destructive' :
                            'default'
                          }
                          className="text-xs"
                        >
                          {segment.segment_type.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      {segment.description && (
                        <p className="text-gray-600 mb-3 break-words">
                          {segment.description}
                        </p>
                      )}
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Code className="h-4 w-4" />
                          <span>{segment.step_count} steps</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Target className="h-4 w-4" />
                          <span>Used {segment.usage_count} times</span>
                        </span>
                        {segment.tags.length > 0 && (
                          <span className="flex items-center space-x-1">
                            <Hash className="h-4 w-4" />
                            <span>{segment.tags.join(', ')}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            // Load full segment details including steps
                            const response = await axios.get(
                              `${BACKEND_URL}/api/flow-testing/segments/${segment.id}`,
                              { headers: getAuthHeaders() }
                            );
                            setEditingSegment(response.data);
                            setShowSegmentModal(true);
                          } catch (error) {
                            console.error('Failed to load segment details:', error);
                            showError('Failed to load segment details');
                          }
                        }}
                        disabled={loading}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDeleteSegmentModal(segment)}
                        className="text-red-600 hover:text-red-700"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Executions Tab */}
        <TabsContent value="executions" className="space-y-4">
          {selectedTest ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <IDTooltip id="FLT-04">
                  <h3 className="text-xl font-semibold">
                    Execution History: {selectedTest.name}
                  </h3>
                </IDTooltip>
                <div className="flex items-center space-x-2">
                  {executions.some(exec => (exec.status === 'running' || exec.status === 'pending') && exec.status !== 'paused') && (
                    <div className="flex items-center space-x-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-md text-sm">
                      <div className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                      <span>Live updating...</span>
                    </div>
                  )}
                  <Button 
                    onClick={() => loadExecutions(selectedTest.id)}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedTest(null);
                      setActiveTab('tests'); // Switch back to tests tab
                    }}
                    variant="outline"
                  >
                    Back to Tests
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {executions.filter(execution => execution != null).map((execution) => (
                  <Card key={execution.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          {getStatusIcon(execution.status)}
                          <div>
                            <div className="font-medium">Execution #{execution?.execution_number || 'N/A'}</div>
                            <div className="text-xs text-gray-500 font-mono">ID: {execution?.id || 'Unknown'}</div>
                          </div>
                          <Badge className={getStatusColor(execution.status)}>
                            {execution.status}
                          </Badge>
                          <div className="flex items-center space-x-2">
                            {getBrowserIcon(execution?.browser)}
                            <span className="text-sm text-gray-600 capitalize">
                              {execution?.browser || 'Unknown'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getDeviceIcon(execution?.device_type)}
                            <span className="text-sm text-gray-600 capitalize">
                              {execution?.device_type || 'Unknown'}
                            </span>
                          </div>
                          {execution.is_headless ? (
                            <EyeOff className="h-4 w-4 text-gray-500" title="Headless" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-500" title="Headed" />
                          )}
                        </div>

                        <div className="text-sm text-gray-600 space-y-1">
                          <p>📅 {execution?.started_at ? new Date(execution.started_at).toLocaleString() : 'Unknown'}</p>
                          {execution.finished_at ? (
                            <>
                              <p>🏁 Finished: {new Date(execution.finished_at).toLocaleString()}</p>
                              {execution.duration_ms && (
                                <p>⏱️ Duration: {(execution.duration_ms / 1000).toFixed(2)}s</p>
                              )}
                            </>
                          ) : (
                            <p>🔄 {execution.status === 'running' ? 'Currently running...' : 
                                   execution.status === 'paused' ? 'Paused - click Resume to continue' : 
                                   execution.status === 'cancelled' ? 'Cancelled by user' : 'In progress...'}</p>
                          )}
                          <p>
                            ✅ Passed: {execution.passed_assertions} | 
                            ❌ Failed: {execution.failed_assertions} | 
                            📝 Steps: {execution.total_steps}
                          </p>
                          {execution.status === 'error' && (
                            <p className="text-red-600 text-sm">
                              ⚠️ Error occurred during execution
                            </p>
                          )}
                          {execution.status === 'cancelled' && (
                            <p className="text-gray-600 text-sm">
                              ⏹️ Execution was cancelled by user
                            </p>
                          )}
                          {execution.status === 'paused' && (
                            <p className="text-orange-600 text-sm">
                              ⏸️ Execution is paused - click Resume to continue
                            </p>
                          )}
                        </div>

                        {/* Detailed Execution Information */}
                        <div className="mt-4 space-y-3">
                          {/* Error Message Display */}
                          {execution.error_message && (
                            <Alert className="border-red-200 bg-red-50">
                              <AlertTriangle className="h-4 w-4 text-red-600" />
                              <AlertDescription className="text-red-800">
                                <div className="font-semibold mb-1">Execution Error:</div>
                                <div className="text-sm">{execution.error_message}</div>
                              </AlertDescription>
                            </Alert>
                          )}

                          {/* Console Logs */}
                          {execution.console_logs && execution.console_logs.length > 0 && (
                            <details className="bg-gray-50 rounded-lg p-3">
                              <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                                Console Logs ({execution.console_logs.length})
                              </summary>
                              <div className="max-h-40 overflow-y-auto space-y-1">
                                {execution.console_logs.map((log, index) => (
                                  <div key={index} className="text-xs font-mono">
                                    <span className="text-gray-500">[{log.type}]</span>{' '}
                                    <span className="text-gray-800">{log.text}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}

                          {/* Network Logs */}
                          {execution.network_logs && execution.network_logs.length > 0 && (
                            <details className="bg-gray-50 rounded-lg p-3">
                              <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                                Network Logs ({execution.network_logs.length})
                              </summary>
                              <div className="max-h-40 overflow-y-auto space-y-1">
                                {execution.network_logs.map((log, index) => (
                                  <div key={index} className="text-xs font-mono">
                                    <span className="text-red-600">[{log.status || 'ERROR'}]</span>{' '}
                                    <span className="text-gray-800">{log.url}</span>
                                    {log.error && <div className="text-red-600 ml-4">→ {log.error}</div>}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}

                          {/* Execution Results Details */}
                          {execution.results && execution.results.length > 0 && (
                            <details className="bg-blue-50 rounded-lg p-3">
                              <summary className="cursor-pointer font-medium text-blue-700 mb-2">
                                Execution Details ({execution.results.length} results)
                              </summary>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {execution.results.map((result, index) => (
                                  <div key={index} className="border-l-2 border-blue-300 pl-3">
                                    <div className="flex items-center space-x-2">
                                      {getStatusIcon(result.status)}
                                      <span className="text-sm font-medium">{result.message}</span>
                                    </div>
                                    {result.execution_time_ms && (
                                      <div className="text-xs text-gray-600">
                                        Execution time: {result.execution_time_ms}ms
                                      </div>
                                    )}
                                    {result.error_details && (
                                      <div className="text-xs text-red-600 mt-1">
                                        Error: {JSON.stringify(result.error_details)}
                                      </div>
                                    )}
                                    {result.screenshot_path && (
                                      <div className="mt-2">
                                        <Screenshot 
                                          screenshotPath={result.screenshot_path}
                                          alt="Step result screenshot"
                                          className="w-32 h-20 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                                          onClick={() => openScreenshotInNewTab(result.screenshot_path)}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>

                        {/* Show Execution Mode */}
                        <div className="mt-3 text-sm">
                          <span className="inline-flex items-center space-x-1">
                            {execution.is_headless ? (
                              <>
                                <EyeOff className="h-3 w-3" />
                                <span className="text-gray-600">Headless Mode</span>
                              </>
                            ) : (
                              <>
                                <Eye className="h-3 w-3" />
                                <span className="text-gray-600">Headed Mode (Debug)</span>
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Cancel button for running, pending, or paused executions */}
                        {(execution.status === 'running' || execution.status === 'pending' || execution.status === 'paused') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCancelExecutionModal(execution.id)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                          >
                            <Square className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                        )}

                        {/* Pause button for running executions */}
                        {execution.status === 'running' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => pauseExecution(execution.id)}
                            disabled={loading}
                            className="text-orange-600 hover:text-orange-700 border-orange-300 hover:border-orange-400"
                          >
                            <Pause className="h-4 w-4 mr-1" />
                            Pause
                          </Button>
                        )}

                        {/* View Live button for running/pending headed executions */}
                        {(execution.status === 'running' || execution.status === 'pending') && !execution.is_headless && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setLiveViewExecution(execution);
                              setShowLiveView(true);
                            }}
                            className="text-purple-600 hover:text-purple-700 border-purple-300 hover:border-purple-400"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Live
                          </Button>
                        )}

                        {/* View Recording button for completed headed executions */}
                        {execution.finished_at && !execution.is_headless && execution.video_path && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              window.open(`${BACKEND_URL}/api/flow-testing/executions/${execution.id}/video`, '_blank');
                            }}
                            className="text-blue-600 hover:text-blue-700 border-blue-300 hover:border-blue-400"
                          >
                            <Video className="h-4 w-4 mr-1" />
                            View Recording
                          </Button>
                        )}

                        {/* Replay button for completed headed executions */}
                        {execution.finished_at && !execution.is_headless && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setReplayExecution(execution);
                              setShowReplayView(true);
                            }}
                            className="text-purple-600 hover:text-purple-700 border-purple-300 hover:border-purple-400"
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Replay
                          </Button>
                        )}

                        {/* Resume button for paused executions */}
                        {execution.status === 'paused' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resumeExecution(execution.id)}
                            disabled={loading}
                            className="text-blue-600 hover:text-blue-700 border-blue-300 hover:border-blue-400"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Resume
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewExecutionDetails(execution.id)}
                          disabled={loading}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => loadExecutions(selectedTest.id)}
                          disabled={loading}
                        >
                          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}

                {executions.length === 0 && (
                  <Card className="p-8 text-center">
                    <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No execution history yet</p>
                  </Card>
                )}
                
                {/* Load More Button */}
                {executions.length > 0 && hasMoreExecutions && (
                  <div className="flex justify-center mt-4">
                    <Button
                      onClick={loadMoreExecutions}
                      disabled={loadingMoreExecutions}
                      variant="outline"
                      className="w-full max-w-md"
                    >
                      {loadingMoreExecutions ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Loading more executions...
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-2" />
                          Load More Executions
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Show total count if no more to load */}
                {executions.length > 0 && !hasMoreExecutions && (
                  <div className="text-center mt-4 text-sm text-gray-500">
                    All {executions.length} execution(s) loaded
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Play className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Select a test from the Tests tab to view its execution history</p>
            </Card>
          )}
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-4">
          {loadingSchedules ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-3 animate-spin" />
              <p className="text-gray-600">Loading schedules...</p>
            </div>
          ) : schedules.length > 0 ? (
            /* Show schedules list immediately when schedules exist */
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <IDTooltip id="FLT-07">
                  <h3 className="text-lg font-semibold text-gray-900">Flow Test Schedules ({schedules.length})</h3>
                </IDTooltip>
                <Button
                  onClick={() => loadSchedules()}
                  variant="outline"
                  size="sm"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <div className="grid gap-4">
                {schedules.map((schedule) => (
                  <Card key={schedule.id} className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          schedule.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                        }`} />
                        <div>
                          <h4 className="font-semibold text-gray-900">{schedule.test_name}</h4>
                          <p className="text-xs text-gray-400 font-mono select-all cursor-pointer" title="Click to select full ID">
                            ID: {schedule.id}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge variant="outline" className={
                              schedule.schedule_type === 'one_time' ? 'border-blue-500 text-blue-700' : 'border-purple-500 text-purple-700'
                            }>
                              {schedule.schedule_type === 'one_time' ? 'One-time' : `${schedule.recurrence_pattern} recurring`}
                            </Badge>
                            <Badge variant="outline">
                              {schedule.timezone ? (schedule.timezone.includes('/') ? schedule.timezone.split('/')[1].replace('_', ' ') : schedule.timezone) : 'UTC'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            // Find the test and view its history
                            let test = flowTests.find(t => t.id === schedule.test_id);
                            
                            // If test not in current list, fetch it directly
                            if (!test) {
                              try {
                                const response = await axios.get(
                                  `${BACKEND_URL}/api/flow-testing/tests/${schedule.test_id}`,
                                  { headers: getAuthHeaders() }
                                );
                                test = response.data;
                              } catch (error) {
                                console.error('Could not load test:', error);
                                return;
                              }
                            }
                            
                            if (test) {
                              setSelectedTest(test);
                              loadExecutions(test.id);
                              setActiveTab('executions');
                            }
                          }}
                          className="text-blue-600"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          History
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleSchedule(schedule.id)}
                          className={schedule.status === 'active' ? 'text-orange-600' : 'text-green-600'}
                        >
                          {schedule.status === 'active' ? 'Pause' : 'Activate'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => openDeleteScheduleModal(schedule)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Next Run:</span>
                        <p>{schedule.next_run ? (() => {
                          try {
                            const nextRun = schedule.next_run;
                            const utcDate = new Date(nextRun.includes('Z') ? nextRun : nextRun + 'Z');
                            const options = { 
                              timeZone: schedule.timezone || 'America/New_York',
                              year: 'numeric',
                              month: '2-digit', 
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            };
                            return utcDate.toLocaleString('en-US', options);
                          } catch (error) {
                            return 'Invalid date';
                          }
                        })() : 'Not scheduled'}</p>
                      </div>
                      <div>
                        <span className="font-medium">Executions:</span>
                        <p>{schedule.successful_executions || 0} passed, {schedule.failed_executions || 0} failed</p>
                      </div>
                      <div>
                        <span className="font-medium">Last Execution:</span>
                        {schedule.latestExecution && schedule.latestExecution.executed_at ? (
                          <div className="space-y-1">
                            <p className="flex items-center space-x-2">
                              <Badge variant="outline" className={
                                schedule.latestExecution.status === 'PASS' || schedule.latestExecution.status === 'passed' 
                                  ? 'border-green-500 text-green-700' 
                                  : 'border-red-500 text-red-700'
                              }>
                                {schedule.latestExecution.status === 'PASS' || schedule.latestExecution.status === 'passed' ? 'PASS' : 'FAIL'}
                              </Badge>
                              {schedule.latestExecution.executionNumber && (
                                <span className="text-xs text-gray-500">
                                  Execution #{schedule.latestExecution.executionNumber}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(() => {
                                try {
                                  const executedTimeString = schedule.latestExecution.executed_at;
                                  const executedTime = new Date(executedTimeString + (executedTimeString.includes('Z') ? '' : 'Z'));
                                  const options = {
                                    timeZone: schedule.timezone || 'America/New_York',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  };
                                  return executedTime.toLocaleString('en-US', options);
                                } catch (error) {
                                  return 'Invalid date';
                                }
                              })()}
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">No executions yet</p>
                        )}
                      </div>
                    </div>

                    {/* Additional execution details */}
                    {schedule.latestExecution && schedule.total_executions > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-4">
                            <span>
                              <span className="font-medium">Total Runs:</span> {schedule.total_executions}
                            </span>
                            <span>
                              <span className="font-medium">Success Rate:</span> {
                                schedule.total_executions > 0 
                                  ? Math.round((schedule.successful_executions / schedule.total_executions) * 100)
                                  : 0
                              }%
                            </span>
                            {schedule.last_run && (
                              <span>
                                <span className="font-medium">Last Run:</span> {
                                  (() => {
                                    const lastRunTime = new Date(schedule.last_run + (schedule.last_run.includes('Z') ? '' : 'Z'));
                                    const now = new Date();
                                    const diffMs = now - lastRunTime;
                                    const diffMins = Math.round(diffMs / (1000 * 60));
                                    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
                                    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                                    
                                    if (diffMins < 60) return `${diffMins}m ago`;
                                    if (diffHours < 24) return `${diffHours}h ago`;
                                    return `${diffDays}d ago`;
                                  })()
                                }
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            /* Show informational view when no schedules exist */
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <IDTooltip id="FLT-07">
                  <h3 className="text-lg font-semibold text-gray-900">Flow Test Schedules</h3>
                </IDTooltip>
              </div>
              <Card className="p-12 text-center">
                <Calendar className="h-16 w-16 text-purple-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Schedule Your Flow Tests
                </h3>
                <p className="text-gray-600 mb-6">
                  Set up automated test execution with flexible scheduling options
                </p>
                <div className="grid grid-cols-2 gap-4 text-left mb-6">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">One-time scheduled runs</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Daily, weekly, monthly recurring</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Email & in-app notifications</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Timezone support</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Click the "Schedule" button next to any test to get started
                </p>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Environments Tab */}
        <TabsContent value="environments" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <IDTooltip id="FLT-05">
              <h3 className="text-lg font-semibold">Test Environments</h3>
            </IDTooltip>
          </div>
          <div className="flex justify-end mb-4">
            <Dialog open={showEnvironmentModal} onOpenChange={setShowEnvironmentModal}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Environment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Environment</DialogTitle>
                  <p className="text-sm text-gray-500">Configure environment settings and credential profiles for testing</p>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Name *</label>
                      <Input
                        placeholder="e.g., Production, Staging, Development"
                        value={environmentForm.name}
                        onChange={(e) => setEnvironmentForm({...environmentForm, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <Textarea
                        placeholder="Brief description of this environment"
                        value={environmentForm.description}
                        onChange={(e) => setEnvironmentForm({...environmentForm, description: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Base URL *</label>
                      <Input
                        placeholder="https://app.example.com"
                        value={environmentForm.base_url}
                        onChange={(e) => setEnvironmentForm({...environmentForm, base_url: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Variables (JSON)</label>
                      <Textarea
                        placeholder='{"api_key": "xxx", "timeout": "30"}'
                        value={environmentVariablesText}
                        onChange={(e) => setEnvironmentVariablesText(e.target.value)}
                        className="font-mono text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                  
                  {/* Credential Profiles Section */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="block text-sm font-medium">Credential Profiles</label>
                        <p className="text-xs text-gray-500">Add session cookies for authenticated testing</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddProfileForm(true)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Profile
                      </Button>
                    </div>
                    
                    {/* List of added profiles */}
                    {credentialProfiles.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {credentialProfiles.map((profile, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                <User className="h-4 w-4 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{profile.name}</p>
                                <p className="text-xs text-gray-500">
                                  {profile.cookies?.length || 0} cookies
                                  {profile.is_default && <span className="ml-2 text-green-600">(Default)</span>}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-600 hover:text-gray-700 hover:bg-gray-100"
                                onClick={() => {
                                  // Populate form with profile data for editing
                                  setNewProfileForm({
                                    name: profile.name,
                                    description: profile.description || '',
                                    cookies: JSON.stringify(profile.cookies || [], null, 2),
                                    headers: JSON.stringify(profile.headers || {}, null, 2),
                                    is_default: profile.is_default || false
                                  });
                                  setEditingProfileIndex(idx);
                                  setShowAddProfileForm(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setCredentialProfiles(credentialProfiles.filter((_, i) => i !== idx));
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add/Edit Profile Form */}
                    {showAddProfileForm && (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm">
                            {editingProfileIndex !== null ? 'Edit Credential Profile' : 'New Credential Profile'}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowAddProfileForm(false);
                              setEditingProfileIndex(null);
                              setNewProfileForm({ name: '', description: '', cookies: '', headers: '', is_default: false });
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Profile Name *</label>
                          <Input
                            placeholder="e.g., Admin User, Regular User"
                            value={newProfileForm.name}
                            onChange={(e) => setNewProfileForm({...newProfileForm, name: e.target.value})}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Session Cookies (JSON Array) *</label>
                          <Textarea
                            placeholder='[{"name": "session_id", "value": "abc123", "domain": ".example.com"}]'
                            value={newProfileForm.cookies}
                            onChange={(e) => setNewProfileForm({...newProfileForm, cookies: e.target.value})}
                            className="font-mono text-xs"
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Custom Headers (JSON, optional)</label>
                          <Input
                            placeholder='{"Authorization": "Bearer xxx"}'
                            value={newProfileForm.headers}
                            onChange={(e) => setNewProfileForm({...newProfileForm, headers: e.target.value})}
                            className="font-mono text-xs"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="is_default_profile"
                            checked={newProfileForm.is_default}
                            onChange={(e) => setNewProfileForm({...newProfileForm, is_default: e.target.checked})}
                            className="rounded"
                          />
                          <label htmlFor="is_default_profile" className="text-xs">Set as default profile</label>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (!newProfileForm.name.trim()) {
                              showWarning('Profile name is required');
                              return;
                            }
                            try {
                              const cookies = newProfileForm.cookies.trim() ? JSON.parse(newProfileForm.cookies) : [];
                              const headers = newProfileForm.headers.trim() ? JSON.parse(newProfileForm.headers) : {};
                              
                              // If this is set as default, remove default from others
                              let updatedProfiles = [...credentialProfiles];
                              if (newProfileForm.is_default) {
                                updatedProfiles = updatedProfiles.map(p => ({...p, is_default: false}));
                              }
                              
                              if (editingProfileIndex !== null) {
                                // Update existing profile
                                updatedProfiles[editingProfileIndex] = {
                                  ...updatedProfiles[editingProfileIndex],
                                  name: newProfileForm.name.trim(),
                                  description: newProfileForm.description.trim(),
                                  cookies,
                                  headers,
                                  is_default: newProfileForm.is_default
                                };
                                showSuccess('Profile updated successfully');
                              } else {
                                // Add new profile
                                updatedProfiles.push({
                                  name: newProfileForm.name.trim(),
                                  description: newProfileForm.description.trim(),
                                  cookies,
                                  headers,
                                  is_default: newProfileForm.is_default
                                });
                              }
                              
                              setCredentialProfiles(updatedProfiles);
                              setShowAddProfileForm(false);
                              setEditingProfileIndex(null);
                              setNewProfileForm({ name: '', description: '', cookies: '', headers: '', is_default: false });
                            } catch (err) {
                              showWarning('Invalid JSON format in cookies or headers');
                            }
                          }}
                          disabled={!newProfileForm.name.trim()}
                        >
                          {editingProfileIndex !== null ? 'Update Profile' : 'Add Profile'}
                        </Button>
                      </div>
                    )}
                    
                    {credentialProfiles.length === 0 && !showAddProfileForm && (
                      <div className="text-center py-4 text-gray-500 text-sm border rounded-lg bg-gray-50">
                        No credential profiles added. Click "Add Profile" to add session cookies.
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowEnvironmentModal(false);
                        resetEnvironmentForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={createEnvironment}
                      disabled={loading || !environmentForm.name.trim() || !environmentForm.base_url.trim()}
                    >
                      {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                      Create Environment
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {environments.length === 0 ? (
            <Card className="p-8 text-center">
              <Globe className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">No environments configured</p>
              <p className="text-sm text-gray-500">Create an environment to define base URLs and variables for your tests</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {environments.map((env) => (
                <Card key={env.id} className="p-3 sm:p-4 md:p-6 overflow-hidden">
                  <div className="space-y-3">
                    {/* Header with title and buttons */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-semibold truncate pr-2" title={env.name}>
                          {env.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditEnvironment(env)}
                          disabled={loading}
                          className="h-8 w-8 p-0 sm:w-auto sm:px-3"
                        >
                          <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="ml-1 hidden sm:inline text-xs">Edit</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteEnvironmentModal(env)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={loading}
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Description */}
                    {env.description && (
                      <p className="text-sm text-gray-600 break-words leading-relaxed" 
                         style={{
                           display: '-webkit-box',
                           WebkitLineClamp: 2,
                           WebkitBoxOrient: 'vertical',
                           overflow: 'hidden'
                         }}>
                        {env.description}
                      </p>
                    )}

                    {/* Base URL */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs sm:text-sm">
                        <Globe className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                        <span className="font-medium text-gray-700 flex-shrink-0">Base URL:</span>
                      </div>
                      <div className="pl-5 sm:pl-6">
                        <code className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded break-all block">
                          {env.base_url}
                        </code>
                      </div>
                    </div>

                    {/* Variables */}
                    {Object.keys(env.variables).length > 0 && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <Settings className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                          <span className="font-medium text-gray-700 flex-shrink-0">Variables:</span>
                        </div>
                        <div className="pl-5 sm:pl-6">
                          <div className="space-y-1">
                            {Object.entries(env.variables).map(([key, value]) => (
                              <div key={key} className="text-xs sm:text-sm">
                                <code className="bg-gray-100 px-2 py-1 rounded break-all block">
                                  <span className="font-medium text-gray-700">{key}:</span>{' '}
                                  <span className="text-gray-600">{value}</span>
                                </code>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Schedule Test Execution</DialogTitle>
            </DialogHeader>
            {selectedTestForSchedule && (
              <div className="space-y-6">
                {/* Test Information */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Brain className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-900">{selectedTestForSchedule.name}</p>
                      <p className="text-sm text-purple-700">{selectedTestForSchedule.description || 'Flow Test'}</p>
                    </div>
                  </div>
                </div>

                {/* Schedule Type */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-3">
                    Schedule Type
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        scheduleForm.schedule_type === 'one_time'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setScheduleForm({...scheduleForm, schedule_type: 'one_time'})}
                    >
                      <div className="flex items-center space-x-3">
                        <Calendar className="h-5 w-5 text-blue-500" />
                        <div>
                          <p className="font-medium">One-time</p>
                          <p className="text-sm text-gray-600">Run once at a specific time</p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        scheduleForm.schedule_type === 'recurring'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setScheduleForm({...scheduleForm, schedule_type: 'recurring'})}
                    >
                      <div className="flex items-center space-x-3">
                        <RefreshCw className="h-5 w-5 text-purple-500" />
                        <div>
                          <p className="font-medium">Recurring</p>
                          <p className="text-sm text-gray-600">Run on a regular schedule</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* One-time Schedule Fields */}
                {scheduleForm.schedule_type === 'one_time' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Scheduled Date & Time
                    </label>
                    <Input
                      type="datetime-local"
                      value={scheduleForm.scheduled_datetime}
                      onChange={(e) => setScheduleForm({...scheduleForm, scheduled_datetime: e.target.value})}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </div>
                )}

                {/* Recurring Schedule Fields */}
                {scheduleForm.schedule_type === 'recurring' && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">
                        Recurrence Pattern
                      </label>
                      <select
                        value={scheduleForm.recurrence_pattern}
                        onChange={(e) => setScheduleForm({...scheduleForm, recurrence_pattern: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-md"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-2">
                          Start Date
                        </label>
                        <Input
                          type="date"
                          value={scheduleForm.start_date}
                          onChange={(e) => setScheduleForm({...scheduleForm, start_date: e.target.value})}
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-2">
                          Time of Day
                        </label>
                        <Input
                          type="time"
                          value={scheduleForm.time_of_day}
                          onChange={(e) => setScheduleForm({...scheduleForm, time_of_day: e.target.value})}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">
                        End Date (Optional)
                      </label>
                      <Input
                        type="date"
                        value={scheduleForm.end_date}
                        onChange={(e) => setScheduleForm({...scheduleForm, end_date: e.target.value})}
                        placeholder="Leave blank to run indefinitely"
                      />
                    </div>
                  </div>
                )}

                {/* Timezone Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Timezone
                  </label>
                  <select
                    value={scheduleForm.timezone}
                    onChange={(e) => setScheduleForm({...scheduleForm, timezone: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  >
                    <option value="America/New_York">Eastern Time (EST/EDT)</option>
                    <option value="America/Chicago">Central Time (CST/CDT)</option>
                    <option value="America/Denver">Mountain Time (MST/MDT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PST/PDT)</option>
                    <option value="UTC">UTC</option>
                    <option value="Europe/London">London (GMT/BST)</option>
                    <option value="Europe/Paris">Paris (CET/CEST)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Asia/Shanghai">Shanghai (CST)</option>
                    <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                  </select>
                </div>

                {/* Notification Settings */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-3">
                    Notifications
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={scheduleForm.email_notifications}
                        onChange={(e) => setScheduleForm({...scheduleForm, email_notifications: e.target.checked})}
                        className="rounded"
                      />
                      <div>
                        <span className="text-sm font-medium">Email Notifications</span>
                        <p className="text-xs text-gray-600">Get email alerts when scheduled tests complete</p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={scheduleForm.in_app_notifications}
                        onChange={(e) => setScheduleForm({...scheduleForm, in_app_notifications: e.target.checked})}
                        className="rounded"
                      />
                      <div>
                        <span className="text-sm font-medium">In-App Notifications</span>
                        <p className="text-xs text-gray-600">Show notifications in the app when tests complete</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowScheduleModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={createSchedule}
                    disabled={loading}
                    className="flex-1"
                  >
                    {loading ? 'Creating...' : 'Create Schedule'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}


      {/* Live View Modal */}
      {showLiveView && liveViewExecution && (
        <Dialog open={showLiveView} onOpenChange={setShowLiveView}>
          <DialogContent className="max-w-[95vw] h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 py-4 border-b border-gray-200">
              <DialogTitle className="flex items-center space-x-2">
                <Eye className="h-5 w-5 text-purple-600" />
                <span>Live Execution - #{liveViewExecution.execution_number}</span>
                <Badge className="bg-green-100 text-green-700">
                  <div className="flex items-center space-x-1">
                    <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span>Live</span>
                  </div>
                </Badge>
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-hidden px-6 py-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
                {/* Left: Live Browser View (2/3 width) */}
                <div className="lg:col-span-2 flex flex-col h-full min-h-0">
                  <div className="bg-gray-900 rounded-lg p-4 flex-1 min-h-0">
                    <div className="h-full bg-gray-800 rounded flex items-center justify-center relative overflow-hidden">
                      <LiveScreenshotViewer executionId={liveViewExecution.id} />
                    </div>
                  </div>

                  {/* Execution Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm mt-4">
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <Badge className="ml-2">{liveViewExecution.status}</Badge>
                    </div>
                    <div>
                      <span className="text-gray-600">Browser:</span>
                      <span className="ml-2 capitalize">{liveViewExecution.browser}</span>
                    </div>
                  </div>
                </div>

                {/* Right: Live Steps Feed (1/3 width) */}
                <div className="lg:col-span-1 h-full min-h-0">
                  <LiveStepsFeed executionId={liveViewExecution.id} testName={selectedTest?.name} />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}


      {/* Replay View Modal */}
      {showReplayView && replayExecution && (
        <ReplayModal 
          execution={replayExecution} 
          onClose={() => setShowReplayView(false)}
          testName={selectedTest?.name}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Flow Test
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the test and all its execution history.
            </DialogDescription>
          </DialogHeader>
          
          {testToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {testToDelete.name}
                </p>
                {testToDelete.description && (
                  <p className="text-sm text-gray-600">
                    {testToDelete.description}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to delete this flow test?
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTestToDelete(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={deleteFlowTest}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Test
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Segment Confirmation Modal */}
      <Dialog open={showDeleteSegmentModal} onOpenChange={setShowDeleteSegmentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Test Segment
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the test segment. Existing tests that use this segment will not be affected.
            </DialogDescription>
          </DialogHeader>
          
          {segmentToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {segmentToDelete.name}
                </p>
                {segmentToDelete.description && (
                  <p className="text-sm text-gray-600">
                    {segmentToDelete.description}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to delete this test segment?
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteSegmentModal(false);
                    setSegmentToDelete(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={deleteTestSegment}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Segment
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Execution Confirmation Modal */}
      <Dialog open={showCancelExecutionModal} onOpenChange={setShowCancelExecutionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Cancel Execution
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The execution will be stopped immediately.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to cancel this execution? This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelExecutionModal(false);
                  setExecutionToCancel(null);
                }}
                disabled={loading}
              >
                No, Keep Running
              </Button>
              <Button
                onClick={cancelExecution}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XIcon className="h-4 w-4 mr-2" />
                    Yes, Cancel Execution
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Environment Confirmation Modal */}
      <Dialog open={showDeleteEnvironmentModal} onOpenChange={setShowDeleteEnvironmentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Environment
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the environment and all its variables.
            </DialogDescription>
          </DialogHeader>
          
          {environmentToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {environmentToDelete.name}
                </p>
                {environmentToDelete.description && (
                  <p className="text-sm text-gray-600">
                    {environmentToDelete.description}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to delete this environment?
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteEnvironmentModal(false);
                    setEnvironmentToDelete(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={deleteEnvironment}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Environment
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Step Confirmation Modal */}
      <Dialog open={showDeleteStepModal} onOpenChange={setShowDeleteStepModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Step
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The step will be permanently removed from the test.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to delete this step? The remaining steps will be reordered automatically.
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteStepModal(false);
                  setStepToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={deleteStep}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Step
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Schedule Confirmation Modal */}
      <Dialog open={showDeleteScheduleModal} onOpenChange={setShowDeleteScheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Schedule
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the schedule.
            </DialogDescription>
          </DialogHeader>
          
          {scheduleToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {scheduleToDelete.schedule_type === 'one_time' ? 'One-time' : 'Recurring'} Schedule
                </p>
                <p className="text-xs text-gray-600">
                  {scheduleToDelete.schedule_type === 'one_time' 
                    ? `Scheduled for: ${new Date(scheduleToDelete.scheduled_datetime).toLocaleString()}`
                    : `Recurring: ${scheduleToDelete.recurrence_pattern}`
                  }
                </p>
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to delete this schedule?
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteScheduleModal(false);
                    setScheduleToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={deleteSchedule}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Schedule
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Live Test Recorder */}
      <LiveRecorder
        isOpen={showLiveRecorder}
        onClose={() => {
          setShowLiveRecorder(false);
          setTestToResume(null);
          // Refresh the test list - use false to load from beginning
          loadFlowTests(false);
        }}
        user={user}
        environments={environments}
        existingTest={testToResume}
      />

      {/* VNC-based Live Recorder (Beta) */}
      {showVNCRecorder && (
        <VNCLiveRecorder
          testId={testToResume?.id}
          initialUrl={testToResume?.lastUrl || testToResume?.steps?.[0]?.value || ''}
          existingSteps={testToResume?.steps || []}
          isResuming={!!testToResume?.id}
          testName={testToResume?.name || ''}
          onClose={() => {
            setShowVNCRecorder(false);
            setTestToResume(null);
          }}
          onSave={async (steps, testName, startingUrl) => {
            try {
              setLoading(true);
              
              // Start with a navigation step to the starting URL
              const formattedSteps = [];
              
              // Check if steps already have a navigation step at the beginning
              // This happens when resuming a recording
              const hasExistingNavigation = steps.length > 0 && 
                (steps[0].type === 'navigation' || steps[0].type === 'navigate');
              
              // Add navigation step first if we have a starting URL AND no existing navigation
              if (startingUrl && !hasExistingNavigation) {
                formattedSteps.push({
                  order: 1,
                  action: 'navigate',
                  selector: { type: 'url', value: startingUrl, fallback_selectors: [], confidence_score: 1.0 },
                  value: startingUrl,
                  description: `Navigate to ${startingUrl}`,
                  wait_before: 0,
                  wait_after: 1000,
                  timeout: 30000,
                  is_optional: false,
                  assertions: []
                });
              }
              
              // Convert VNC steps to flow test step format
              steps.forEach((step, index) => {
                // Skip recorder_ready steps - they're internal
                if (step.type === 'recorder_ready') {
                  return;
                }
                
                // Build selector object if we have a selector string
                let selectorObj = null;
                if (step.selector) {
                  // Determine selector type based on format
                  let selectorType = 'css';
                  if (step.selector.startsWith('#')) {
                    selectorType = 'css';
                  } else if (step.selector.startsWith('[data-testid')) {
                    selectorType = 'test_id';
                  } else if (step.selector.startsWith('[placeholder')) {
                    selectorType = 'placeholder';
                  } else if (step.selector.startsWith('//') || step.selector.startsWith('xpath=')) {
                    selectorType = 'xpath';
                  } else if (step.selector.startsWith('text=')) {
                    selectorType = 'text';
                  }
                  
                  // Convert step.selectors array to fallback_selectors format
                  // step.selectors is array of { type, selector, priority }
                  const fallbackSelectors = [];
                  if (step.selectors && Array.isArray(step.selectors)) {
                    // Skip the first one (primary) and add the rest as fallbacks
                    step.selectors.slice(1).forEach(sel => {
                      fallbackSelectors.push({
                        type: sel.type || 'css',
                        value: sel.selector
                      });
                    });
                  }
                  
                  selectorObj = {
                    type: selectorType,
                    value: step.selector,
                    fallback_selectors: fallbackSelectors,
                    confidence_score: 1.0
                  };
                }
                
                // Calculate order (starting from 2 if we have navigation step, else 1)
                const stepOrder = formattedSteps.length + 1;
                
                let stepData = {
                  order: stepOrder,
                  action: 'click', // Default action
                  selector: selectorObj,
                  value: '',
                  description: '',
                  wait_before: 0,
                  wait_after: 500,
                  timeout: 30000,
                  is_optional: false,
                  assertions: []
                };
                
                // Map VNC step types to flow test actions
                switch (step.type) {
                  case 'click':
                    stepData.action = 'click';
                    stepData.description = step.text || `Click on ${step.selector || 'element'}`;
                    break;
                  case 'type':
                    stepData.action = 'type';
                    stepData.value = step.value || '';
                    stepData.description = `Type "${step.value || ''}" into ${step.selector || 'field'}`;
                    break;
                  case 'select':
                    stepData.action = 'select';
                    stepData.value = step.value || '';
                    stepData.description = `Select "${step.text || ''}" in ${step.selector || 'dropdown'}`;
                    break;
                  case 'check':
                    stepData.action = 'click'; // Use click for checkbox
                    stepData.description = `${step.checked ? 'Check' : 'Uncheck'} ${step.selector || 'checkbox'}`;
                    break;
                  case 'assert':
                    // Map assertion types to valid backend actions
                    const assertActionMap = {
                      'title': 'assert_text',
                      'text': 'assert_text',
                      'visible': 'assert_visible',
                      'exists': 'assert_exists'
                    };
                    stepData.action = assertActionMap[step.assertType] || 'assert_text';
                    stepData.expected_text = step.expected || '';
                    stepData.assertion_type = step.assertType;
                    stepData.description = `Assert ${step.assertType}: ${step.expected || step.selector || ''}`;
                    break;
                  case 'navigation':
                    stepData.action = 'navigate';
                    stepData.value = step.url || '';
                    stepData.selector = { type: 'url', value: step.url || '', fallback_selectors: [], confidence_score: 1.0 };
                    stepData.description = `Navigate to ${step.url || 'page'}`;
                    break;
                  case 'submit':
                    stepData.action = 'click';
                    stepData.description = step.buttonText || 'Submit form';
                    break;
                  case 'keypress':
                    stepData.action = 'custom';
                    stepData.value = step.key || 'Enter';
                    stepData.description = `Press ${step.key || 'Enter'} on ${step.selector || 'element'}`;
                    break;
                  default:
                    stepData.action = 'custom';
                    stepData.description = `${step.type} on ${step.selector || 'element'}`;
                }
                
                formattedSteps.push(stepData);
              });
              
              // If we're editing an existing test, update it
              if (testToResume?.id) {
                await axios.put(
                  `${BACKEND_URL}/api/flow-testing/tests/${testToResume.id}`,
                  {
                    name: testName || testToResume.name,
                    steps: formattedSteps
                  },
                  { headers: getAuthHeaders() }
                );
                showSuccess('Test updated successfully!');
              } else {
                // Create a new test
                const newTest = {
                  name: testName || 'VNC Recorded Test',
                  description: `Recorded via VNC Live Recorder on ${new Date().toLocaleString()}`,
                  tags: ['vnc-recorded'],
                  steps: formattedSteps,
                  global_assertions: [],
                  configuration: {
                    browser: 'chromium',
                    device_type: 'desktop',
                    viewport_width: 1920,
                    viewport_height: 1080,
                    is_headless: true,
                    enable_screenshots: true,
                    enable_video: false,
                    timeout: 30000
                  },
                  environment_id: null,
                  project_id: selectedProjectFilter || null,
                  test_variables: {},
                  sensitive_variables: []
                };
                
                await axios.post(
                  `${BACKEND_URL}/api/flow-testing/tests`,
                  newTest,
                  { headers: getAuthHeaders() }
                );
                
                // Track creation
                trackFlowTestCreation('vnc-recorder', testName);
                showSuccess('Test created successfully!');
              }
              
              await loadFlowTests(false);
              setShowVNCRecorder(false);
              setTestToResume(null);
            } catch (error) {
              console.error('Failed to save VNC test:', error);
              const errorMsg = error.response?.data?.detail;
              // Handle Pydantic validation errors
              if (Array.isArray(errorMsg)) {
                showError(errorMsg.map(e => e.msg || e).join(', '));
              } else {
                showError(errorMsg || 'Failed to save test');
              }
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {/* Full Page Flow Test Editor */}
      {showFullPageEditor && editingTest && (
        <FlowTestEditor
          test={editingTest}
          projects={projects}
          loading={loading}
          onClose={() => {
            setShowFullPageEditor(false);
            setEditingTest(null);
            resetTestForm();
          }}
          onResumeVNCRecording={(test) => {
            // Check if resuming from a specific step (Continue from step feature)
            const resumeUrl = test.resumeUrl;
            const steps = test.steps || [];
            let lastUrl = resumeUrl || '';
            
            console.log('[Resume VNC] Test:', test.name, 'resumeFromStepIndex:', test.resumeFromStepIndex, 'resumeUrl:', resumeUrl);
            
            // If no specific resumeUrl, find the LAST URL from the test steps
            if (!lastUrl) {
              for (let i = 0; i < steps.length; i++) {
                const step = steps[i];
                // Check for navigate action
                if (step.action === 'navigate') {
                  const url = step.value || step.selector?.value;
                  if (url) {
                    lastUrl = url;
                    console.log('[Resume VNC] Found navigate URL at step', i, ':', lastUrl);
                  }
                }
              }
            }
            
            console.log('[Resume VNC] Final URL to resume at:', lastUrl);
            
            setShowFullPageEditor(false);
            setTestToResume({
              id: test.id,
              name: test.name,
              description: test.description,
              steps: steps,
              lastUrl: lastUrl,
              resumeFromStepIndex: test.resumeFromStepIndex
            });
            setShowVNCRecorder(true);
          }}
          onSave={async (updatedTest) => {
            try {
              setLoading(true);
              // Update the test form with the data from the editor
              setTestForm(prev => ({
                ...prev,
                name: updatedTest.name,
                description: updatedTest.description,
                project_id: updatedTest.project_id,
                steps: updatedTest.steps,
                test_variables: updatedTest.test_variables
              }));
              // Call the existing update function
              await axios.put(
                `${BACKEND_URL}/api/flow-testing/tests/${editingTest.id}`,
                {
                  name: updatedTest.name,
                  description: updatedTest.description,
                  project_id: updatedTest.project_id,
                  steps: updatedTest.steps,
                  test_variables: updatedTest.test_variables,
                  tags: editingTest.tags || [],
                  configuration: editingTest.configuration || {}
                },
                { headers: getAuthHeaders() }
              );
              showSuccess('Test updated successfully!');
              setShowFullPageEditor(false);
              setEditingTest(null);
              loadFlowTests(false);
            } catch (error) {
              console.error('Error updating test:', error);
              showError('Failed to update test');
            } finally {
              setLoading(false);
            }
          }}
          onQuickRun={async (updatedTest, mode) => {
            try {
              setLoading(true);
              
              // Save the test first with current edits
              await axios.put(
                `${BACKEND_URL}/api/flow-testing/tests/${editingTest.id}`,
                {
                  name: updatedTest.name,
                  description: updatedTest.description,
                  project_id: updatedTest.project_id,
                  steps: updatedTest.steps,
                  test_variables: updatedTest.test_variables,
                  tags: editingTest.tags || [],
                  configuration: editingTest.configuration || {}
                },
                { headers: getAuthHeaders() }
              );
              
              // Close the editor first
              setShowFullPageEditor(false);
              setEditingTest(null);
              
              // Get fresh test data for execution
              const testResponse = await axios.get(
                `${BACKEND_URL}/api/flow-testing/tests/${editingTest.id}`,
                { headers: getAuthHeaders() }
              );
              const freshTest = testResponse.data;
              
              // Determine headless mode based on quick run option
              let isHeadless = true;
              if (mode === 'headless') {
                isHeadless = true;
              } else if (mode === 'headed') {
                isHeadless = false;
              }
              
              // Use test's saved configuration with mode override
              const executionConfig = {
                browser: freshTest.configuration?.browser || 'chromium',
                device_type: freshTest.configuration?.device_type || 'desktop',
                device_name: freshTest.configuration?.device_name || null,
                environment_id: freshTest.environment_id || null,
                is_headless: isHeadless,
                debug_mode: false,
                parallel_executions: 1,
                viewport_width: freshTest.configuration?.viewport_width || 1920,
                viewport_height: freshTest.configuration?.viewport_height || 1080
              };
              
              // Execute with the specified mode
              await axios.post(
                `${BACKEND_URL}/api/flow-testing/tests/${editingTest.id}/execute`,
                { configuration_override: executionConfig },
                { headers: getAuthHeaders() }
              );
              
              // Track execution
              trackFlowTestExecution(editingTest.id, updatedTest.name);
              
              // Show success message with mode indicator
              const modeLabel = mode === 'headless' ? ' (Headless - Faster)' : ' (Headed - Visual)';
              showSuccess(`Test saved & execution started${modeLabel}! Check the Executions tab for results.`);
              
              // Refresh the executions list for this specific test
              await loadExecutions(editingTest.id);
              
              // Set the selected test so executions are filtered correctly
              setSelectedTest(freshTest);
              
              // Switch to executions tab
              setActiveTab('executions');
              
              // Also refresh the tests list
              loadFlowTests(false);
            } catch (error) {
              console.error('Error running test:', error);
              showError('Failed to save and run test: ' + (error.response?.data?.detail || error.message));
            } finally {
              setLoading(false);
            }
          }}
          onManageVariables={(variables) => {
            setTestForm(prev => ({ ...prev, test_variables: variables }));
            analyzeVariableUsage();
            setShowVariablesModal(true);
          }}
          externalVariables={testForm.test_variables}
          testSegments={testSegments}
          onInsertSegment={(segmentId, position) => {
            insertTestSegment(editingTest.id, segmentId, position);
          }}
          onCreateSegmentFromSteps={(selectedStepIndices) => {
            setSelectedStepsForSegment(selectedStepIndices);
            setShowCreateSegmentFromStepsModal(true);
          }}
        />
      )}

    </div>
  );
};

// Wrap the component with error boundary
const FlowTestingWithErrorBoundary = ({ user, selectedTestFromDashboard, onTestSelected }) => {
  return (
    <FlowTestingErrorBoundary>
      <FlowTesting 
        user={user} 
        selectedTestFromDashboard={selectedTestFromDashboard}
        onTestSelected={onTestSelected}
      />
    </FlowTestingErrorBoundary>
  );
};

export default FlowTestingWithErrorBoundary;